/**
 * Message viewer component -- displays full message in sandboxed iframe.
 *
 * Passive renderer. The list owns "what's focused"; this module exposes
 * `showMessage(accountId, uid, folderId, envelope?)` and `clearViewer()`
 * as the only ways content reaches the pane. There is no subscription —
 * if the list never tells the viewer about a focus change, the viewer
 * simply doesn't update. That's the property that makes summary↔preview
 * drift impossible: there is exactly one path into this pane.
 */

import { getMessage, updateFlags, allowRemoteContent, flagSenderOrDomain, trustSenderOrDomain, getAttachment, openAttachment, getMessageSource, addContact, listContacts, upsertContact, unsubscribeOneClick, addPreferredContact, onEvent, subscribeStore } from "../lib/api-client.js";
import { isSenderApproved, noteApprovedLocally, refreshApprovedSenders } from "../lib/approved-senders.js";
import { buildInviteCard } from "./invite-card.js";
import { showContextMenu } from "./context-menu.js";
import type { MenuItem } from "./context-menu.js";
import type { ListMessage } from "../lib/message-state.js";
import { updateMessageFlags as stateUpdateFlags } from "../lib/message-state.js";
import { setRowSeen } from "./message-list.js";
import { setSeen, seenOf, formatSender, displayNameClaimsOtherAddress } from "@bobfrankston/mailx-types";

// ── "Copy image" bridge ──
// The preview iframe encodes the image and posts the bytes back; these track
// the outstanding request. Keyed by id because a user can hit Copy image on a
// second picture before the first encode finishes.
const pendingImageCopies = new Map<number, (r: { blob?: Blob; error?: string }) => void>();
let imageCopySeq = 0;
const IMAGE_COPY_TIMEOUT_MS = 8000;

window.addEventListener("message", (e) => {
    const d: any = e.data;
    if (!d || d.type !== "previewImageBytes") return;
    const resolve = pendingImageCopies.get(d.reqId);
    if (!resolve) return;               // timed out already
    pendingImageCopies.delete(d.reqId);
    resolve({ blob: d.blob, error: d.error });
});

/** Ask the preview iframe to encode the image the user right-clicked. Resolves
 *  with a PNG blob, or an error string when the iframe can't read it (a remote
 *  image taints the canvas — caller falls back to the daemon). */
function requestImageFromPreview(sourceWindow: Window | null, src: string): Promise<{ blob?: Blob; error?: string }> {
    if (!sourceWindow) return Promise.resolve({ error: "preview window is gone" });
    const reqId = ++imageCopySeq;
    return new Promise(resolve => {
        const done = (r: { blob?: Blob; error?: string }): void => { clearTimeout(timer); resolve(r); };
        const timer = setTimeout(() => {
            pendingImageCopies.delete(reqId);
            resolve({ error: "the preview did not answer in time" });
        }, IMAGE_COPY_TIMEOUT_MS);
        pendingImageCopies.set(reqId, done);
        try { sourceWindow.postMessage({ type: "previewCommand", cmd: "copyImage", reqId, src }, "*"); }
        catch (err: any) { pendingImageCopies.delete(reqId); done({ error: err?.message || String(err) }); }
    });
}

/** Currently displayed message (for reply/forward) */
let currentMessage: any = null;
let currentAccountId: string = "";
// Blob URLs created for attachment drag-out (revoked when the next message's
// chips render, so they don't accumulate across the session).
const dragOutUrls: string[] = [];
let showMessageGeneration = 0; // Cancel stale fetches
let retryCount = 0;
/** Last envelope handed in by the list — used for envelope-first paint
 *  on retry, where we don't have a fresh row click to pass it again. */
let lastEnvelope: ListMessage | null = null;

/** Parsed-body LRU. Click → render must be synchronous; the only way to
 *  honor that is to keep the parsed result in WebView memory keyed by
 *  (accountId, uid). Bounded so a long session doesn't eat hundreds of
 *  MB of decoded HTML. Hit = render now, no IPC, no spinner, no
 *  "Loading…". Miss = paint the envelope's preview as a transient body
 *  and swap in the real one when the IPC returns (also caches). */
const PARSED_CACHE_LIMIT = 64;
const parsedCache = new Map<string, any>(); // key: `${accountId}:${folderId}:${uid}` → full message obj
// folderId is required in the key: IMAP UIDs are per-folder, so the SAME
// numeric UID (e.g. 196) refers to entirely different messages in INBOX vs
// Outbox vs Sent. Keying on (accountId, uid) alone caused the Outbox header
// to render with the body of an INBOX message at the same UID.
function parsedCacheGet(accountId: string, folderId: number | undefined, uid: number): any | null {
    if (folderId == null) return null; // no folderId → can't safely identify; skip cache
    const key = `${accountId}:${folderId}:${uid}`;
    const v = parsedCache.get(key);
    if (!v) return null;
    parsedCache.delete(key);
    parsedCache.set(key, v);
    return v;
}
function parsedCachePut(accountId: string, folderId: number | undefined, uid: number, msg: any): void {
    if (folderId == null) return; // no folderId → don't poison the cache
    const key = `${accountId}:${folderId}:${uid}`;
    parsedCache.delete(key);
    parsedCache.set(key, msg);
    while (parsedCache.size > PARSED_CACHE_LIMIT) {
        const oldest = parsedCache.keys().next().value;
        if (oldest === undefined) break;
        parsedCache.delete(oldest);
    }
}
/** Drop one entry — called when flags change so re-render reads fresh state. */
export function invalidateParsedCache(accountId: string, folderId: number | undefined, uid: number): void {
    if (folderId == null) return;
    parsedCache.delete(`${accountId}:${folderId}:${uid}`);
}

/** Session-scoped "user clicked Allow on this message" set. Once a uuid
 *  is in here, any subsequent showMessage call treats `hasRemoteContent`
 *  as false regardless of what the daemon returned — covers the race
 *  where the allowlist persist hasn't reached disk yet, the daemon
 *  re-parses and reports remote content again, and a folder refresh
 *  re-renders the banner on top of the unblocked iframe (Bob 2026-05-09:
 *  "still showing the blocked header" while content was loaded). */
const sessionAllowedRemote = new Set<string>();

/** Per-uid recent body-fetch errors. Populated by the bodyFetchError
 *  listener so the "No content" code path can show the real reason
 *  instead of a generic placeholder. Keyed `${accountId}:${uid}`. */
const recentFetchErrors = new Map<string, { error: string; transient: boolean; when: number }>();
function markSessionAllowed(accountId: string, uid: number): void {
    sessionAllowedRemote.add(`${accountId}:${uid}`);
}
function isSessionAllowed(accountId: string, uid: number): boolean {
    return sessionAllowedRemote.has(`${accountId}:${uid}`);
}

export function getCurrentMessage(): { accountId: string; message: any } | null {
    if (!currentMessage) return null;
    return { accountId: currentAccountId, message: currentMessage };
}

/** Build the body-context menu items (right-click outside a link in the
 *  preview iframe). Called from app.ts's window message listener after the
 *  iframe inline script postMessages a "previewContextMenu" event with the
 *  current selection. Translate routes to the configured AI provider via
 *  aiTransform; "Translate via Google" opens translate.google.com in the
 *  default browser as a no-account-needed alternative. */
export function showPreviewBodyMenu(absX: number, absY: number, selectedText: string, sourceWindow: Window | null, linkUrl?: string, linkText?: string, imgSrc?: string): void {
    const pct = Math.round(previewZoom * 100);
    const runSearch = (query: string): void => {
        const input = document.getElementById("search-input") as HTMLInputElement | null;
        if (!input) return;
        input.value = query;
        input.dispatchEvent(new Event("input"));
        input.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
        input.focus();
        // Narrow mode shows ONE pane. A search launched from the viewer used
        // to leave the (now stale/empty) viewer on screen — no search bar,
        // no results, "a blank screen and no clue what is happening" (Bob
        // 2026-07-13, Android). Flip to the list pane so the populated
        // search bar and the result list are what the user actually sees.
        document.getElementById("message-viewer")?.classList.remove("narrow-active");
        document.getElementById("message-list")?.classList.remove("narrow-hidden");
    };
    // Send a command back into the iframe (Copy, Select all) — the iframe
    // owns its own document and these only work in the iframe's context.
    const tellIframe = (cmd: string): void => {
        try { sourceWindow?.postMessage({ type: "previewCommand", cmd }, "*"); } catch { /* */ }
    };
    const fullBody = (): string => {
        try {
            for (const f of Array.from(document.querySelectorAll("iframe"))) {
                if ((f as HTMLIFrameElement).contentWindow === sourceWindow) {
                    return (f as HTMLIFrameElement).contentDocument?.body?.innerText || "";
                }
            }
        } catch { /* */ }
        return "";
    };
    const items: MenuItem[] = [];
    // Link actions first (when right-click was over a link). Quora-style
    // pages are wall-to-wall links — without this, body actions like
    // Translate were unreachable because every long-press hit a link.
    if (linkUrl) {
        const guessName = (() => {
            try { const u = new URL(linkUrl); const last = u.pathname.split("/").pop() || ""; return last && last.includes(".") ? last : ""; }
            catch { return ""; }
        })();
        items.push(
            { label: "Open link in browser", action: () => { const api = (window as any).mailxapi; if (api?.openExternal) api.openExternal(linkUrl); else window.open(linkUrl, "_blank", "noopener,noreferrer"); } },
            { label: guessName ? `Save link as "${guessName}"…` : "Save link as…", action: () => {
                const a = document.createElement("a"); a.href = linkUrl;
                a.download = guessName || ""; a.style.display = "none";
                document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 1000);
            } },
            { label: "Copy link URL", action: async () => {
                try { await navigator.clipboard.writeText(linkUrl); } catch { prompt("URL:", linkUrl); }
            } },
            { label: "Copy link text", action: async () => {
                try { await navigator.clipboard.writeText(linkText || linkUrl); } catch { prompt("Text:", linkText || linkUrl); }
            } },
            { label: "", action: () => {}, separator: true },
        );
    }
    // Image actions when the right-click was over an <img>. Lets the user pull
    // an image out of a received message and into a reply (Copy image → Ctrl+V
    // in compose, which embeds it) or save it to disk. Bob 2026-06-01.
    if (imgSrc) {
        const guessImgName = (() => {
            try { const u = new URL(imgSrc); const last = u.pathname.split("/").pop() || ""; return last && last.includes(".") ? last : "image.png"; }
            catch { return "image.png"; }
        })();
        items.push(
            { label: "Copy image", action: async () => {
                // Three ways in, because no single one covers every image a
                // letter can carry (Bob 2026-08-11: right-click → copy just
                // failed). In order of what works most often:
                //   1. the preview iframe encodes its own already-decoded
                //      <img> — the only path that works for inline cid:/data:
                //      images, and it costs no network at all;
                //   2. the parent fetches the src — fine for data: and for
                //      hosts that send CORS headers;
                //   3. the daemon fetches it — remote images are cross-origin
                //      with no CORS headers, so the WebView can never read
                //      their bytes, but Node has no such restriction.
                const attempts: string[] = [];

                const writePng = async (png: Blob): Promise<void> => {
                    await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
                };
                // Transcode to PNG via canvas — WebView2's clipboard image
                // write is reliable for PNG; arbitrary source types (webp,
                // gif, jpeg) may not round-trip directly.
                const toPng = async (blob: Blob): Promise<Blob> => {
                    if (blob.type === "image/png") return blob;
                    const bmp = await createImageBitmap(blob);
                    const canvas = document.createElement("canvas");
                    canvas.width = bmp.width; canvas.height = bmp.height;
                    canvas.getContext("2d")!.drawImage(bmp, 0, 0);
                    return await new Promise((res, rej) =>
                        canvas.toBlob(b => b ? res(b) : rej(new Error("toBlob failed")), "image/png"));
                };

                // 1. Ask the preview to encode it.
                const fromPreview = await requestImageFromPreview(sourceWindow, imgSrc);
                if (fromPreview.blob) {
                    try { await writePng(fromPreview.blob); return; }
                    catch (e: any) { attempts.push(`clipboard write: ${e?.message || e}`); }
                } else {
                    attempts.push(`preview: ${fromPreview.error}`);
                }

                // 2. Fetch it from here.
                try {
                    const resp = await fetch(imgSrc);
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    await writePng(await toPng(await resp.blob()));
                    return;
                } catch (e: any) {
                    attempts.push(`direct fetch: ${e?.message || e}`);
                }

                // 3. Let the daemon fetch it (remote images, no CORS).
                if (/^https?:/i.test(imgSrc)) {
                    try {
                        const { fetchRemoteImage } = await import("../lib/api-client.js");
                        const r = await fetchRemoteImage(imgSrc);
                        if (!r?.base64) throw new Error(r?.error || "no data returned");
                        const bytes = Uint8Array.from(atob(r.base64), c => c.charCodeAt(0));
                        await writePng(await toPng(new Blob([bytes], { type: r.contentType || "image/png" })));
                        return;
                    } catch (e: any) {
                        attempts.push(`via rmfmail: ${e?.message || e}`);
                    }
                }

                // Name what was tried — a bare "couldn't copy" leaves nothing
                // to act on, and these three failure modes need different fixes.
                alert(`Couldn't copy that image.\n\n${attempts.map(a => `• ${a}`).join("\n")}`);
            } },
            { label: `Save image as "${guessImgName}"…`, action: () => {
                const a = document.createElement("a"); a.href = imgSrc;
                a.download = guessImgName; a.style.display = "none";
                document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 1000);
            } },
            { label: "", action: () => {}, separator: true },
        );
    }
    items.push(
        { label: "Copy", action: () => tellIframe("copy") },
        { label: "Select all", action: () => tellIframe("selectAll") },
    );
    if (selectedText) {
        items.push(
            { label: "", action: () => {}, separator: true },
            { label: `Search messages for "${selectedText.length > 40 ? selectedText.slice(0, 40) + "…" : selectedText}"`,
              action: () => runSearch(selectedText) },
            { label: "Copy as quoted (> prefix)",
              action: async () => {
                  const quoted = selectedText.split(/\r?\n/).map(l => "> " + l).join("\n");
                  try { await navigator.clipboard.writeText(quoted); } catch { /* */ }
              } },
        );
    }
    const senderAddr = currentMessage?.from?.address || "";
    if (senderAddr) {
        items.push({ label: `Search messages from ${senderAddr}`, action: () => runSearch(`from:${senderAddr}`) });
    }
    items.push(
        { label: "", action: () => {}, separator: true },
        { label: selectedText ? "Translate selection (AI)" : "Translate message (AI)",
          action: () => translateAndShow(selectedText || fullBody()) },
        { label: selectedText ? "Translate selection (Google Translate)" : "Translate message (Google Translate)",
          action: () => {
              const text = (selectedText || fullBody()).slice(0, 4500); // GT URL has practical limits
              const url = `https://translate.google.com/?sl=auto&tl=en&op=translate&text=${encodeURIComponent(text)}`;
              const api = (window as any).mailxapi;
              if (api?.openExternal) api.openExternal(url); else window.open(url, "_blank", "noopener,noreferrer");
          } },
        { label: "", action: () => {}, separator: true },
        // Zoom group — current level (resets on click) first, then in / out.
        { label: `Zoom: ${pct}% — reset`, action: () => { for (const f of Array.from(document.querySelectorAll("iframe"))) { const d = (f as HTMLIFrameElement).contentDocument; if (d) { setZoom(1, d); break; } } } },
        { label: "Zoom in  (+)", action: () => { for (const f of Array.from(document.querySelectorAll("iframe"))) { const d = (f as HTMLIFrameElement).contentDocument; if (d) { setZoom(previewZoom + ZOOM_STEP, d); break; } } } },
        { label: "Zoom out (−)", action: () => { for (const f of Array.from(document.querySelectorAll("iframe"))) { const d = (f as HTMLIFrameElement).contentDocument; if (d) { setZoom(previewZoom - ZOOM_STEP, d); break; } } } },
        { label: "", action: () => {}, separator: true },
        { label: "Print… (Ctrl+P)", action: () => printCurrentMessage() },
        { label: "Save message (.eml)…", action: () => { void saveCurrentMessageAsEml(); } },
        { label: "Save message as HTML…", action: () => { void saveCurrentMessageAsHtml(); } },
    );
    showContextMenu(absX, absY, items);
}

/** How long the viewer waits for a body before it stops counting and
 *  explains itself. Long enough that a slow Dovecot read or a cold Gmail
 *  batch still lands inside it; short enough that nobody watches a counter
 *  tick past a minute wondering whether the app is alive. */
const WAIT_GIVE_UP_SECS = 45;

/**
 * Replace the elapsed-time counter with something that tells the user what
 * happened and what they can still do.
 *
 * The counter existed to distinguish "slow" from "wedged" (v1.0.x, Bob
 * 2026-05-24) and it does — but only for the first half-minute. Past that it
 * is a progress bar for a process that is not running: nothing retries, no
 * error was reported, and the number just grows. This says so.
 */
function renderBodyFetchGaveUp(
    bodyEl: HTMLElement,
    accountId: string,
    uid: number,
    folderId: number | undefined,
    secs: number,
    previewText: string,
    specialUse?: string,
    envelope?: any,
): void {
    // A Gmail account addresses bodies by a uid derived from the Gmail
    // message id — a ~10^14 number. A small sequential uid on such an
    // account means this row came from the IMAP side (server search, an
    // older sync), and the Gmail API fetcher has no way to name it. That is
    // not a slow network and retrying will not help, so say which it is
    // instead of offering false hope.
    const looksImapUidOnApiAccount = uid > 0 && uid < 1_000_000_000;
    const why = looksImapUidOnApiAccount
        ? `This message was indexed over IMAP, but its account fetches bodies through the Gmail API, which addresses messages by a different id. The body can't be requested for this row.`
        : `The server accepted the request but never returned the body.`;
    const snippet = previewText
        ? `<div class="mv-preview-placeholder">${escapeHtml(previewText)}<div class="mv-tear-line"><span>✂ snippet — this is all that arrived ✂</span></div></div>`
        : "";
    bodyEl.innerHTML = `${snippet}<div class="mv-system-message mv-system-error">
        <div class="mv-system-tag">rmfmail</div>
        <div class="mv-system-title">Body didn't arrive after ${secs}s — not still loading</div>
        <div class="mv-system-body">${escapeHtml(why)}
        <div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap">
            <button id="mv-body-retry" class="tb-btn">Try again</button>
        </div></div></div>`;
    bodyEl.querySelector("#mv-body-retry")?.addEventListener("click", () => {
        invalidateParsedCache(accountId, folderId, uid);
        showMessage(accountId, uid, folderId, specialUse, true, envelope).catch(() => { /* reported by the next render */ });
    });
    // No "View source" button here — the viewer toolbar already has Source,
    // and a second control that dispatches an event nothing listens for is
    // the silent-no-op pattern this session spent the day removing.
}

/** Toggle preview-fullscreen mode. CSS rule on body.fullscreen-preview hides
 *  toolbar / rail / folder tree / list / statusbar and expands the viewer to
 *  the full viewport. Exits via second toggle, Esc, or the floating ✕. */
export function toggleFullscreenPreview(): void {
    const body = document.body;
    const on = !body.classList.contains("fullscreen-preview");
    // Never ENTER fullscreen with nothing to read. An empty viewer expanded
    // over the whole window — no toolbar, no list, no label — reads as a
    // crash, not a mode (Bob 2026-06-05 "double-clicked ... got this screen";
    // 2026-07-29 "strange full screen mode and stayed strange till I exited").
    if (on && !currentMessage) return;
    body.classList.toggle("fullscreen-preview", on);
    try { (window as any).mailxapi?.logClientEvent?.(`[fullscreen] preview ${on ? "ON" : "OFF"}`); } catch { /* diagnostic only */ }
    let exit = document.getElementById("mv-exit-fullscreen");
    if (on) {
        // Transient hint — same pattern as a browser's "press Esc to exit
        // full screen" toast, so an accidental double-click is self-explaining.
        if (!document.getElementById("mv-fullscreen-hint")) {
            const hint = document.createElement("div");
            hint.id = "mv-fullscreen-hint";
            hint.textContent = "Fullscreen reading — press Esc or double-click to exit";
            hint.style.cssText = "position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:6000;background:rgba(0,0,0,0.7);color:#fff;padding:8px 16px;border-radius:16px;font-size:13px;pointer-events:none;opacity:1;transition:opacity 0.5s;";
            document.body.appendChild(hint);
            setTimeout(() => { hint.style.opacity = "0"; }, 2500);
            setTimeout(() => hint.remove(), 3100);
        }
        if (!exit) {
            exit = document.createElement("button");
            exit.id = "mv-exit-fullscreen";
            exit.textContent = "✕";
            exit.title = "Exit fullscreen (Esc or double-tap)";
            exit.style.cssText = "position:fixed;top:8px;right:8px;z-index:6000;width:36px;height:36px;border-radius:50%;border:none;background:rgba(0,0,0,0.55);color:#fff;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.4);";
            exit.addEventListener("click", () => toggleFullscreenPreview());
            document.body.appendChild(exit);
        }
        // Esc handler — installed once, reads the class on each press.
        if (!(window as any).__mvFullscreenEsc) {
            const onEsc = (e: KeyboardEvent) => {
                if (e.key === "Escape" && document.body.classList.contains("fullscreen-preview")) {
                    e.preventDefault();
                    toggleFullscreenPreview();
                }
            };
            document.addEventListener("keydown", onEsc, true);
            (window as any).__mvFullscreenEsc = onEsc;
        }
    } else {
        exit?.remove();
        document.getElementById("mv-fullscreen-hint")?.remove();
    }
}

/** Kept as an exported no-op for back-compat with app.ts wiring. The
 *  state-subscribe model is gone; list now drives the viewer directly. */
export function initViewer(): void { /* no-op: list owns focus */ }

// Zoom is persisted across messages via localStorage
const ZOOM_KEY = "mailx-preview-zoom";
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3.0;
const ZOOM_STEP = 0.1;
// 1.15 default: smaller than browser-default text felt cramped per user
// feedback 2026-05-20. Ctrl+= / Ctrl+- / Ctrl+wheel / right-click menu all
// override and persist, so the default only matters on first-ever launch or
// after a WebView2 profile wipe.
let previewZoom = clampZoom(parseFloat(localStorage.getItem(ZOOM_KEY) || "1.15"));

function clampZoom(z: number): number {
    if (!Number.isFinite(z) || z <= 0) return 1;
    return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(z * 100) / 100));
}

function applyZoom(doc: Document): void {
    // Zoom lives on <html>, not <body>, because WebView2/Chromium's scroll
    // container is the root element — body.style.zoom leaves the scroll
    // container out of sync with the zoomed content, breaking scrollbar
    // display and wheel scrolling. documentElement.style.zoom keeps them
    // aligned so the iframe scrolls normally at any zoom level.
    if (doc.documentElement) (doc.documentElement.style as any).zoom = String(previewZoom);
}

function setZoom(z: number, doc: Document): void {
    previewZoom = clampZoom(z);
    localStorage.setItem(ZOOM_KEY, String(previewZoom));
    applyZoom(doc);
}

/** Install preview iframe controls: key forwarding to parent, Ctrl+wheel zoom,
 *  keyboard zoom shortcuts (Ctrl+= / Ctrl+- / Ctrl+0), and the right-click menu. */
/** Run AI translate on `text` and show result in a small modal. Disabled
 *  by default — user enables via Settings (translateEnabled in
 *  AutocompleteSettings). When disabled, the modal explains how to enable. */
async function translateAndShow(text: string): Promise<void> {
    if (!text.trim()) return;
    const status = document.getElementById("status-sync");
    if (status) status.textContent = "Translating…";

    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:10000;display:flex;align-items:center;justify-content:center;";
    const modal = document.createElement("div");
    modal.style.cssText = "background:var(--bg, #fff);color:var(--fg, #000);border:1px solid var(--border, #ccc);border-radius:6px;width:560px;max-width:90vw;max-height:70vh;display:flex;flex-direction:column;box-shadow:0 4px 24px rgba(0,0,0,0.3);";
    const header = document.createElement("div");
    header.style.cssText = "padding:10px 14px;border-bottom:1px solid var(--border, #ddd);font-weight:600;display:flex;justify-content:space-between;align-items:center;";
    header.innerHTML = `<span>Translation</span><button style="cursor:pointer;border:0;background:transparent;font-size:16px;" aria-label="Close">×</button>`;
    const body = document.createElement("div");
    body.style.cssText = "flex:1;overflow:auto;padding:12px 14px;white-space:pre-wrap;font-size:13px;line-height:1.45;";
    body.textContent = "Working…";
    modal.appendChild(header);
    modal.appendChild(body);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    header.querySelector("button")?.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    document.addEventListener("keydown", function onKey(e: KeyboardEvent) {
        if (e.key === "Escape") { document.removeEventListener("keydown", onKey); close(); }
    });

    try {
        const { aiTransform } = await import("../lib/api-client.js");
        const r = await aiTransform({ action: "translate", text, targetLang: "en" });
        if (r.text) {
            body.textContent = r.text;
            if (status) status.textContent = "";
        } else {
            // Only offer "go enable it" when that is actually the problem.
            // Translate WAS enabled and the real fault was an unreachable
            // provider; the stock hint sent Bob to a checkbox that was
            // already ticked (2026-08-11).
            const reason = r.reason || "no result";
            const isDisabled = /disabled in settings|not configured/i.test(reason);
            const hint = isDisabled
                ? "Enable AI translate in Settings → AI features (off by default)."
                : /can't reach/i.test(reason)
                    ? "The AI provider isn't answering. Check Settings → AI features, or start the local model server."
                    : "";
            body.innerHTML = `<div style="color:var(--muted, #888);">No result.</div>` +
                `<div style="margin-top:8px;font-size:12px;color:var(--muted, #888);">${escapeHtml(reason)}</div>` +
                (hint ? `<div style="margin-top:14px;font-size:12px;">${hint}</div>` : "");
            if (status) status.textContent = `Translate: ${r.reason || "no result"}`;
        }
    } catch (err: any) {
        body.textContent = `Error: ${err?.message || String(err)}`;
        if (status) status.textContent = `Translate error: ${err?.message || ""}`;
    }
}

// ── Search-match highlighting ──
// Opening a hit from a search should show you WHERE the hit is, not hand you
// a 40-screen newsletter and wish you luck (Bob 2026-08-15). The terms come
// from the search box (app.ts calls setSearchHighlightTerms); the marks are
// painted with the CSS Custom Highlight API, which takes plain Ranges and
// styles them via ::highlight() WITHOUT touching the DOM. That matters here:
// the message body is sanitized, sandboxed, sometimes progressively appended,
// and read back for copy/quote — wrapping matches in <mark> would edit the
// letter itself and leak into every one of those paths.
const HIGHLIGHT_NAME = "mailx-find";
/** Cap on painted ranges. A one-letter term in a megabyte newsletter would
 *  otherwise build tens of thousands of Ranges on the click path. */
const HIGHLIGHT_MAX = 2000;
let searchHighlightTerms: string[] = [];

/** Pull the highlightable words out of a search query. Qualifier values that
 *  address a header (`subject:`) are kept — they're visible in the message —
 *  while `from:`/`to:`/`date:`/`has:`/`is:`/`folder:` are search plumbing, not
 *  body text. `NOT foo` is dropped: it says the word is ABSENT. Quoted phrases
 *  stay whole. Mirrors the qualifier set parsed in mailx-store's db.ts. */
export function parseHighlightTerms(query: string): string[] {
    const parts = (query || "").match(/"[^"]*"|\S+/g) || [];
    const out: string[] = [];
    let negate = false;
    for (const raw of parts) {
        const part = raw.replace(/^"|"$/g, "");
        if (/^(AND|OR)$/i.test(part)) continue;
        if (/^NOT$/i.test(part)) { negate = true; continue; }
        if (negate) { negate = false; continue; }
        if (part.startsWith("-")) continue;                       // -term = exclude
        const q = part.match(/^([a-z]+):(.*)$/i);
        if (q) {
            const [, field, value] = q;
            if (/^subject$/i.test(field) && value) out.push(value.replace(/^"|"$/g, ""));
            continue;                                              // other qualifiers aren't body text
        }
        if (part.length >= 2) out.push(part);
    }
    // Longest first: with both "fox" and "foxes" in the query, matching the
    // longer one first stops the shorter from claiming the same start offset
    // and leaving a stray tail unhighlighted.
    return Array.from(new Set(out.map(t => t.toLowerCase()))).sort((a, b) => b.length - a.length);
}

/** Collect a Range per term occurrence in a document's visible text. */
function collectHighlightRanges(doc: Document, terms: string[]): Range[] {
    const ranges: Range[] = [];
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
        acceptNode(node: Node) {
            const tag = (node.parentElement?.tagName || "").toUpperCase();
            if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") return NodeFilter.FILTER_REJECT;
            return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        },
    });
    for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
        const hay = n.data.toLowerCase();
        // Track claimed spans so two terms can't double-highlight one stretch.
        const taken: Array<[number, number]> = [];
        for (const term of terms) {
            let from = 0;
            for (;;) {
                const at = hay.indexOf(term, from);
                if (at < 0) break;
                const end = at + term.length;
                from = end;
                if (taken.some(([s, e]) => at < e && end > s)) continue;
                taken.push([at, end]);
                const r = doc.createRange();
                r.setStart(n, at);
                r.setEnd(n, end);
                ranges.push(r);
                if (ranges.length >= HIGHLIGHT_MAX) return ranges;
            }
        }
    }
    return ranges;
}

/** Paint (or clear) the highlight inside one preview iframe. Returns the
 *  match count. No-ops on a host without the Custom Highlight API — the
 *  message still renders, it just isn't marked up. */
function applySearchHighlight(iframe: HTMLIFrameElement, scrollToFirst = false): number {
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow as any;
    if (!doc?.body || !win?.CSS?.highlights || typeof win.Highlight !== "function") return 0;
    try {
        win.CSS.highlights.delete(HIGHLIGHT_NAME);
        if (searchHighlightTerms.length === 0) return 0;
        const ranges = collectHighlightRanges(doc, searchHighlightTerms);
        if (ranges.length === 0) return 0;
        win.CSS.highlights.set(HIGHLIGHT_NAME, new win.Highlight(...ranges));
        // Bring the first hit into view when it's below the fold — the point
        // of the feature is not having to hunt. Never scroll for a match that
        // is already visible: yanking a preview the user can already read is
        // the arrival-jump behavior we removed elsewhere.
        if (scrollToFirst) {
            const rect = ranges[0].getBoundingClientRect();
            const h = doc.documentElement.clientHeight || 0;
            if (rect.height > 0 && (rect.top < 0 || rect.bottom > h)) {
                const target = (doc.scrollingElement || doc.documentElement).scrollTop + rect.top - h / 3;
                (doc.scrollingElement || doc.documentElement).scrollTop = Math.max(0, target);
            }
        }
        return ranges.length;
    } catch { return 0; }   // a torn-down iframe mid-render — nothing to mark
}

/** Same paint for the viewer's own chrome (subject line lives outside the
 *  iframe, and it's the field people search most). */
function applyHeaderHighlight(): void {
    const anyWin = window as any;
    if (!anyWin.CSS?.highlights || typeof anyWin.Highlight !== "function") return;
    try {
        anyWin.CSS.highlights.delete(HIGHLIGHT_NAME);
        const subj = document.querySelector(".mv-subject") as HTMLElement | null;
        if (!subj || searchHighlightTerms.length === 0) return;
        const ranges = collectHighlightRangesIn(subj, searchHighlightTerms);
        if (ranges.length) anyWin.CSS.highlights.set(HIGHLIGHT_NAME, new anyWin.Highlight(...ranges));
    } catch { /* header not rendered yet */ }
}

/** collectHighlightRanges scoped to one element of the PARENT document. */
function collectHighlightRangesIn(root: HTMLElement, terms: string[]): Range[] {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const ranges: Range[] = [];
    for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
        const hay = n.data.toLowerCase();
        for (const term of terms) {
            let from = 0;
            for (;;) {
                const at = hay.indexOf(term, from);
                if (at < 0) break;
                from = at + term.length;
                const r = document.createRange();
                r.setStart(n, at);
                r.setEnd(n, from);
                ranges.push(r);
            }
        }
    }
    return ranges;
}

/** Set the terms to highlight in the open message (empty = clear). Called by
 *  app.ts whenever the search box changes or a search tab is restored. */
export function setSearchHighlightTerms(terms: string[]): void {
    const next = terms.filter(Boolean).map(t => t.toLowerCase());
    const same = next.length === searchHighlightTerms.length
        && next.every((t, i) => t === searchHighlightTerms[i]);
    if (same) return;
    searchHighlightTerms = next;
    applyHeaderHighlight();
    for (const f of Array.from(document.querySelectorAll("iframe"))) {
        applySearchHighlight(f as HTMLIFrameElement, true);
    }
}

/** Re-paint after content lands (initial render, progressive text append). */
export function refreshSearchHighlight(iframe: HTMLIFrameElement, scrollToFirst = false): void {
    if (searchHighlightTerms.length === 0) return;
    applySearchHighlight(iframe, scrollToFirst);
    applyHeaderHighlight();
}

function installPreviewControls(iframe: HTMLIFrameElement): void {
    const attach = () => {
        const doc = iframe.contentDocument;
        if (!doc) return;
        // Idempotent per DOCUMENT. A freshly-appended srcdoc iframe first
        // exposes a throwaway `about:blank` document whose readyState is
        // already "complete" — so binding to it is binding to something that
        // is discarded a frame later, and every path below has to run again
        // against the real one. Marking the document (not the iframe) lets us
        // safely attach from both the immediate and the load path.
        if ((doc as any).__mvControlsBound) return;
        (doc as any).__mvControlsBound = true;

        applyZoom(doc);
        // Paint search marks as soon as the text exists — `attach` also runs
        // on `load`, which on a remote-image newsletter is many seconds after
        // the words are readable.
        refreshSearchHighlight(iframe, true);

        doc.addEventListener("keydown", (e) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
            // Zoom is iframe-local — handle here, don't forward.
            if (e.ctrlKey && (e.key === "=" || e.key === "+")) { e.preventDefault(); setZoom(previewZoom + ZOOM_STEP, doc); return; }
            if (e.ctrlKey && e.key === "-") { e.preventDefault(); setZoom(previewZoom - ZOOM_STEP, doc); return; }
            if (e.ctrlKey && e.key === "0") { e.preventDefault(); setZoom(1, doc); return; }
            // Ctrl+A is preview-local too: select the LETTER's text so it can
            // be copied. Forwarding it let the list's select-all-rows grab it
            // even with focus in the preview (Bob 2026-07-18). Don't forward,
            // don't preventDefault — the browser's native select-all inside
            // the iframe selects exactly this document.
            if (e.ctrlKey && !e.altKey && !e.metaKey && (e.key === "a" || e.key === "A")) return;
            // Everything else is FORWARDED BY THE IFRAME ITSELF — the inline
            // script wrapHtmlBody installs posts every keydown to the parent
            // as `previewKey`, and app.ts re-dispatches it on document. This
            // listener must NOT forward as well.
            //
            // It used to, and both routes are live on desktop WebView2 (the
            // srcdoc document is same-origin, so this contentDocument listener
            // attaches, and the inline script runs too). One Ctrl+F in the
            // preview therefore reached app.ts TWICE and opened TWO compose
            // windows — one of which never finished loading and sat blank and
            // "(Not Responding)" until it was killed (Bob 2026-08-23,
            // "Compose: Fwd: FirstCare Slide Deck (Not Responding)"; daemon
            // log shows openCompose-entry mode=forward twice in the same
            // millisecond and two popoutCompose windows). Ctrl+D / Ctrl+P
            // doubled the same way.
            //
            // The inline forwarder is the one that survives, because it is the
            // only one that works on Android WebView, where parent-side
            // contentDocument listeners never fire. Zoom and Ctrl+A stay here:
            // they act on THIS document and the inline script deliberately
            // skips them. (Claude Code 2026-08-23)
        });

        doc.addEventListener("wheel", (e) => {
            if (!e.ctrlKey) return;
            e.preventDefault();
            setZoom(previewZoom + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP), doc);
        }, { passive: false });

        // dblclick fullscreen toggle moved into the iframe's inline script
        // so it's wired by the time the document loads — the parent-side
        // contentDocument listener was racing with the load event and
        // sometimes never attached.

        // Link interception lives in the iframe's own inline <script> (see
        // wrapHtmlBody). That script runs under a CSP nonce so email-body
        // scripts stay blocked while ours forwards taps to the parent frame.

        // Body-context menu was previously built here on doc.contextmenu, but
        // the inline iframe script (wrapHtmlBody) now postMessages the request
        // to the parent unconditionally — see showPreviewContextMenu in app.ts
        // which calls showPreviewBodyMenu below. This route works on every
        // host; the doc-level handler missed cases where WebView2's native
        // menu fired before our parent listener got installed.
    };
    // Bind on BOTH paths, not one or the other: `load` is when the real
    // srcdoc document exists, and the immediate call covers an iframe that
    // is genuinely already loaded (re-render of a live preview). The
    // per-document guard in attach() makes the overlap a no-op — where the
    // old `complete ? attach() : onload` choice could bind the whole set to
    // the discarded about:blank document and never run against the letter.
    iframe.addEventListener("load", attach);
    if (iframe.contentDocument?.readyState === "complete") attach();
    // DOMContentLoaded is the "text is painted" mark (see the _ptick pair at
    // the render site); highlight there too so marks appear with the words
    // rather than after the last tracking pixel resolves.
    queueMicrotask(() => {
        const doc = iframe.contentDocument;
        if (!doc) return;
        if (doc.readyState === "loading") {
            doc.addEventListener("DOMContentLoaded", () => refreshSearchHighlight(iframe, true), { once: true });
        } else {
            refreshSearchHighlight(iframe, true);
        }
    });
}

export function clearViewer(): void {
    currentMessage = null;
    currentAccountId = "";
    lastEnvelope = null;
    showMessageGeneration++;
    const headerEl = document.getElementById("mv-header") as HTMLElement;
    const bodyEl = document.getElementById("mv-body") as HTMLElement;
    const attEl = document.getElementById("mv-attachments") as HTMLElement;
    if (bodyEl) bodyEl.innerHTML = `<div class="mv-empty">Select a message to read</div>`;
    if (headerEl) headerEl.hidden = true;
    if (attEl) attEl.hidden = true;
    // The invite card is a SIBLING of the body (it sits after the header), so
    // clearing bodyEl leaves it behind — a stale meeting hovering over "Select
    // a message to read" is exactly the viewer-must-clear failure.
    document.getElementById("mv-invite")?.remove();
    // Divergence watchdog: highlight and viewer are supposed to be one
    // thing. If a second after a clear some row still shows .selected while
    // the pane shows the placeholder, that's the "message selected but it's
    // asking me to select a message" state (Bob 2026-07-28) — log which
    // rows so the next occurrence is diagnosable, don't silently allow it.
    setTimeout(() => {
        try {
            const stillPlaceholder = !!document.querySelector("#mv-body .mv-empty");
            if (!stillPlaceholder) return;
            const sel = document.querySelectorAll(".ml-row.selected");
            if (sel.length === 0) return;
            const uids = Array.from(sel).slice(0, 5).map(r => `${(r as HTMLElement).dataset.accountId}/${(r as HTMLElement).dataset.uid}`);
            (window as any).mailxapi?.logClientEvent?.("[fate-share] viewer empty but rows still highlighted", { rows: uids });
        } catch { /* watchdog must never throw */ }
    }, 1000);
}

// Viewer-side guard on the viewer-must-clear rule: when the message ON
// DISPLAY is removed (draft discarded from compose, EXPUNGE from another
// client), clear the pane directly. The list's remove-stale flow also
// clears via focus-handoff — but only when the removed row is in the
// CURRENT view; a draft previewed earlier (or any cross-folder removal)
// no-ops there and the pane kept showing the dead message (Bob 2026-07-18
// "I discarded a message but it is still showing in preview").
subscribeStore("*", (ev: any) => {
    if (ev?.kind !== "messageRemoved") return;
    if (!currentMessage) return;
    // uuid is real identity — use it when both sides carry one (store bus
    // events name it `msgUuid`). The (account, uid, folder-wildcard)
    // fallback can match a removal of a DIFFERENT message that shares the
    // uid number in another folder (unified view + server-side moves) and
    // clear the viewer spuriously.
    const evUuid = ev.msgUuid || ev.uuid;
    if (evUuid && (currentMessage as any).uuid) {
        if (ev.accountId === currentAccountId && evUuid === (currentMessage as any).uuid) {
            try { (window as any).mailxapi?.logClientEvent?.("[fate-share] viewed message removed (uuid) — clearing viewer", { uuid: evUuid }); } catch { /* */ }
            clearViewer();
        }
        return;
    }
    const folderMatches = ev.folderId == null || currentMessage.folderId == null
        || ev.folderId === currentMessage.folderId;
    if (ev.accountId === currentAccountId && ev.uid === currentMessage.uid && folderMatches) {
        try { (window as any).mailxapi?.logClientEvent?.("[fate-share] viewed message removed (uid) — clearing viewer", { uid: ev.uid, folderId: ev.folderId ?? null }); } catch { /* */ }
        clearViewer();
    }
});

/** Render the message identified by (accountId, uid, folderId). The
 *  list passes the row's existing envelope as `envelope` so the header +
 *  preview snippet paint *immediately*, before the body fetch returns —
 *  that's the "no Fetching… empty pane" guarantee. On retry, we reuse
 *  the most recently provided envelope. */
/** Confirm-and-delete for received-message attachments. The service does the
 *  local strip synchronously (verified re-parse before any write) and queues
 *  the server-side replace; on success the viewer re-renders from the
 *  now-stripped local copy. Failures leave the message untouched and say so. */
function confirmDeleteAttachments(msg: any, accountId: string, uid: number, ids: number[] | "all", question: string): void {
    const backdrop = document.createElement("div");
    backdrop.className = "mailx-modal-backdrop";
    backdrop.style.cssText = "position:fixed;inset:0;z-index:2100;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;";
    const panel = document.createElement("div");
    panel.style.cssText = "background:var(--color-bg,#fff);color:var(--color-text,#000);border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,0.3);width:min(440px,90vw);padding:16px;display:flex;flex-direction:column;gap:12px;";
    const body = document.createElement("div");
    body.style.cssText = "line-height:1.5;";
    body.textContent = `${question} The attachment is removed from the local copy and from the server copy. The message text is kept. This cannot be undone.`;
    const status = document.createElement("div");
    status.style.cssText = "min-height:1.2em;font-size:0.9em;color:var(--color-text-muted);";
    const row = document.createElement("div");
    row.style.cssText = "display:flex;gap:8px;justify-content:flex-end;";
    const cancel = document.createElement("button");
    cancel.textContent = "Cancel";
    cancel.style.cssText = "padding:6px 14px;";
    cancel.addEventListener("click", () => backdrop.remove());
    const del = document.createElement("button");
    del.textContent = "Delete";
    del.style.cssText = "padding:6px 14px;font-weight:600;background:#c0392b;color:#fff;border:none;border-radius:4px;cursor:pointer;";
    del.addEventListener("click", async () => {
        del.disabled = true;
        cancel.disabled = true;
        status.textContent = "Removing…";
        try {
            const { deleteAttachments } = await import("../lib/api-client.js");
            const r = await deleteAttachments(accountId, uid, msg.folderId, ids);
            backdrop.remove();
            // Re-render from the stripped local copy (chips, paperclip, size).
            void showMessage(accountId, uid, msg.folderId);
            void r;
        } catch (e: any) {
            status.textContent = e?.message || "Delete failed — message unchanged.";
            del.disabled = false;
            cancel.disabled = false;
        }
    });
    row.append(cancel, del);
    panel.append(body, status, row);
    backdrop.append(panel);
    document.body.append(backdrop);
}

// ── Render crash-loop breaker ──
//
// A body big enough to kill the WebView2 renderer takes the whole window with
// it. msger catches that and reloads (install_process_failed_reload), which is
// the right instinct — but the reloaded page restores the same selection,
// renders the same message, and dies again. The loop reads as "rmfmail is not
// responding" (Bob 2026-08-08, a 9.35 MB bounce).
//
// So: write down which message we are ABOUT to render, and clear it once the
// render completes. If that marker is still present at startup, the previous
// attempt never finished — the message is presumed lethal and is not rendered
// again automatically. The user gets a plain explanation and an explicit
// "try anyway", instead of an app that dies on sight of one letter.
//
// sessionStorage (not localStorage) on purpose: it survives the reload that
// follows a renderer crash but not a deliberate restart, so a one-off failure
// can't poison a message forever.
const RENDER_MARK_KEY = "mailx-rendering";
const RENDER_MARK_MAX_AGE_MS = 60_000;
const poisonedMessages = new Set<string>();
const renderKey = (a: string, u: number): string => `${a}:${u}`;

(() => {
    try {
        const raw = sessionStorage.getItem(RENDER_MARK_KEY);
        if (!raw) return;
        sessionStorage.removeItem(RENDER_MARK_KEY);
        const mark = JSON.parse(raw);
        // Stale marks are ignored: a mark older than a minute is far more
        // likely a tab left open mid-render than a crash we just came back from.
        if (!mark?.key || Date.now() - (mark.at || 0) > RENDER_MARK_MAX_AGE_MS) return;
        poisonedMessages.add(mark.key);
        console.error(`[viewer] previous render of ${mark.key} did not complete — the WebView probably died on it; not auto-rendering again`);
    } catch { /* sessionStorage unavailable — no loop protection, no harm */ }
})();

function markRenderStart(accountId: string, uid: number): void {
    try {
        sessionStorage.setItem(RENDER_MARK_KEY, JSON.stringify({ key: renderKey(accountId, uid), at: Date.now() }));
    } catch { /* non-fatal */ }
}
function markRenderDone(): void {
    try { sessionStorage.removeItem(RENDER_MARK_KEY); } catch { /* non-fatal */ }
}

export async function showMessage(accountId: string, uid: number, folderId?: number, specialUse?: string, isRetry = false, envelope?: ListMessage): Promise<void> {
    const gen = ++showMessageGeneration;
    if (!isRetry) retryCount = 0;
    const headerEl = document.getElementById("mv-header") as HTMLElement;
    const bodyEl = document.getElementById("mv-body") as HTMLElement;
    const attEl = document.getElementById("mv-attachments") as HTMLElement;

    // Same-message re-show guard. The list rebuilds on folderCountsChanged
    // (sync events, IDLE, flag updates) and `restoreSelection()` then calls
    // `focusRow()` → `viewerShow()` on the row whose uid matches. If we
    // re-paint here, `bodyEl.innerHTML = …` later in this function destroys
    // the iframe and resets the user's scroll position to top. Skip that
    // entirely when the message identity hasn't changed and we've got a
    // live iframe to keep. Header refresh is harmless (no scroll inside)
    // and keeps flag/read state visually current. Retry path bypasses the
    // guard so transient body fetches still get their delayed paint.
    //
    // Identity check: prefer the stable `uuid`. (uid, folderId) is NOT a
    // reliable identity — uid is per-folder, so a cross-folder search can
    // surface two different messages with the same uid, and a result whose
    // folderId is omitted made the guard mis-fire: the header refreshed to
    // the clicked message but the body kept the previously-shown message
    // (Bob 2026-05-18: the .eml shown "does not match the header"). Fall
    // back to (folderId, uid) only when a uuid isn't available on both
    // sides (e.g. thread-popup envelopes, which carry no uuid) — and then
    // ONLY with a known folderId, since (account, folder, uid) is a real
    // identity but (account, uid) alone is not. A missing folderId is
    // treated as "can't confirm → different message", forcing a re-render:
    // showing a stale unrelated letter (Bob 2026-05-22: "clicked a letter
    // but it's still showing one I'd been looking at") is far worse than a
    // re-render's scroll reset.
    const sameMessage = !!currentMessage && currentAccountId === accountId && (
        (envelope?.uuid && currentMessage.uuid)
            ? envelope.uuid === currentMessage.uuid
            : (folderId !== undefined
                && currentMessage.uid === uid
                && currentMessage.folderId === folderId)
    );
    if (!isRetry && sameMessage && bodyEl.querySelector("iframe")) {
        if (envelope) {
            lastEnvelope = envelope;
            try { renderHeaderFromEnvelope(headerEl, envelope); headerEl.hidden = false; } catch { /* */ }
        }
        return;
    }

    // Click-to-preview timing — answers "why is this slow?" with numbers.
    // Marks: enter / cache-hit-or-miss / IPC-begin / IPC-done / iframe-set /
    // iframe-loaded. Without these the symptom "previews very slow" is
    // unmeasurable.
    const _previewT0 = performance.now();
    const _ptick = (label: string): void => {
        const line = `[preview ${(performance.now() - _previewT0).toFixed(0).padStart(5)} ms] ${label} ${accountId}/uid=${uid}`;
        console.log("  " + line);
        try { (window as any).mailxapi?.logClientEvent?.(line); } catch { /* */ }
    };
    _ptick("showMessage entry");

    // Clear any "current message" identity at the START of a new render —
    // before a single byte is fetched. Action buttons (Source, Edit Draft,
    // attachments) read from `currentMessage` and must NOT serve the
    // previous message's data while this one is still loading. The
    // closures they had pointed at the old message; clearing the shared
    // state forces them to read whatever this render eventually commits.
    currentMessage = null;
    currentAccountId = "";
    // Hide action buttons that depend on the message body — they'll be
    // restored when the new message data arrives.
    const srcBtn = document.getElementById("mv-view-source") as HTMLButtonElement | null;
    if (srcBtn) srcBtn.hidden = true;
    const editBtn = document.getElementById("mv-edit-draft") as HTMLButtonElement | null;
    if (editBtn) editBtn.hidden = true;

    // Envelope-first render: the row the user just clicked already has the
    // subject / from / to / cc / date / preview in the message-state. Use
    // that to populate the header + a snippet placeholder IMMEDIATELY so
    // tapping a message never shows just "Fetching message body..." with
    // nothing actionable. The full getMessage() call (which might block on
    // a slow IMAP body fetch) only fills in the body and attachments.
    const cached = (envelope && envelope.uid === uid && (envelope.accountId || accountId) === accountId)
        ? envelope
        : (lastEnvelope && lastEnvelope.uid === uid && (lastEnvelope.accountId || accountId) === accountId ? lastEnvelope : null);
    if (envelope) lastEnvelope = envelope;

    // Cache lookup BEFORE any placeholder paint. If this message's parsed
    // body is in WebView memory (recently viewed), skip the IPC entirely —
    // render synchronously below.
    const cachedMsg = parsedCacheGet(accountId, folderId, uid);

    if (cached) {
        try { renderHeaderFromEnvelope(headerEl, cached); } catch { /* */ }
        // 2026-05-13: if the envelope has a non-empty `preview`, show it as
        // a miniature body until the parsed HTML arrives. If it's empty
        // (common for mailing-list / quoted-reply messages), show an
        // explicit "Loading body…" so the user never sees a totally blank
        // pane and thinks the click didn't register. Earlier wording chose
        // silence on the theory that "Loading…" is a lie when fast — but
        // an empty pane is a worse lie when the parse is slow.
        if (!cachedMsg) {
            const previewText = (cached.preview || "").trim();
            bodyEl.innerHTML = previewText
                ? `<div class="mv-preview-placeholder">${escapeHtml(previewText)}</div>`
                : `<div class="mv-empty">Loading body…</div>`;
        }
    } else if (!cachedMsg) {
        // No envelope and no cache — show a loading indicator instead of
        // an empty pane. The IPC return will paint over it.
        bodyEl.innerHTML = `<div class="mv-empty">Loading body…</div>`;
        headerEl.hidden = true;
    }
    attEl.hidden = true;

    // Refuse to re-render a message the WebView already died on, unless the
    // user explicitly asks. `isRetry` is the "try anyway" path.
    if (poisonedMessages.has(renderKey(accountId, uid)) && !isRetry) {
        const emlHint = (envelope as any)?.emlPath || "";
        bodyEl.innerHTML = `<div class="mv-system-message mv-system-error">
            <div class="mv-system-tag">mailx</div>
            <div class="mv-system-title">Render failed for this message</div>
            <div class="mv-system-body">The display engine stopped while drawing this message last time, so it hasn't been drawn again automatically.
            Usually this means an unusually large body.${emlHint ? `<br><code style="user-select:all;font-size:0.9em">${escapeHtml(emlHint)}</code>` : ""}
            <br><br><button type="button" id="mv-render-anyway" class="mailx-modal-btn">Try anyway</button></div>
        </div>`;
        document.getElementById("mv-render-anyway")?.addEventListener("click", () => {
            poisonedMessages.delete(renderKey(accountId, uid));
            void showMessage(accountId, uid, folderId, specialUse, true, envelope);
        });
        attEl.hidden = true;
        return;
    }

    try {
        if (cachedMsg) _ptick("parsedCache HIT — sync render");
        else _ptick("parsedCache MISS — IPC begin");
        markRenderStart(accountId, uid);
        const msg = cachedMsg ? cachedMsg : await getMessage(accountId, uid, false, folderId);
        if (!cachedMsg) _ptick(`IPC done (cached=${(msg as any).cached !== false}, ${(msg as any).bodyHtml?.length || 0} bytes html)`);
        // Stale response — a newer showMessage was called while we were fetching
        if (gen !== showMessageGeneration) return;

        // User clicked Allow earlier this session — treat as unblocked
        // regardless of what the daemon currently reports. Covers the
        // race where the allowlist persist is still in flight.
        if (isSessionAllowed(accountId, uid) && (msg as any).hasRemoteContent) {
            (msg as any).hasRemoteContent = false;
        }

        // Body not yet on disk — service kicked off a background fetch and
        // will emit `bodyAvailable` when the bytes land. Show envelope-only
        // (header + preview snippet) and re-render on the event. No timer,
        // no client-side retry; the reconciler retries server-side.
        if ((msg as any).cached === false) {
            try { renderHeaderFromEnvelope(headerEl, msg as any); headerEl.hidden = false; } catch { /* */ }
            // Body genuinely missing from local store — the reconciler is
            // fetching it from IMAP/Gmail. Show preview as a miniature
            // body if available, otherwise a clear "Fetching from server…"
            // so a slow fetch isn't indistinguishable from a hung pane.
            const previewText = ((msg as any).preview || cached?.preview || "").trim();
            // Wait indicator: render an elapsed-time counter alongside the
            // placeholder. A still-counting timer means the fetch is alive
            // but slow; a stalled count means the bodyAvailable event isn't
            // firing (folder_id corruption, iflow desync, Gmail API 429,
            // etc.). Without this, "Fetching body from server…" looked the
            // same at 1 s as at 5 min, and a real wedge was indistinguishable
            // from a normal slow Dovecot read (Bob 2026-05-24).
            const waitStart = Date.now();
            const indicatorHtml = `<span class="mv-wait-elapsed" data-start="${waitStart}">(0s)</span>`;
            bodyEl.innerHTML = previewText
                ? `<div class="mv-preview-placeholder">${escapeHtml(previewText)}<div class="mv-tear-line" aria-label="snippet ends here, full message loading"><span>✂ snippet — fetching full message ✂</span></div><div class="mv-wait-line">Fetching body from server… ${indicatorHtml}</div></div>`
                : `<div class="mv-empty">Fetching body from server… ${indicatorHtml}</div>`;
            const captureGen = gen;
            // 1 Hz tick to update the elapsed counter. Stops automatically
            // when (a) bodyAvailable arrives, (b) generation advances (user
            // navigated), or (c) the element vanishes from the DOM.
            const tick = setInterval(() => {
                if (captureGen !== showMessageGeneration) { clearInterval(tick); return; }
                const span = bodyEl.querySelector<HTMLSpanElement>(".mv-wait-elapsed");
                if (!span) { clearInterval(tick); return; }
                const secs = Math.round((Date.now() - waitStart) / 1000);
                span.textContent = secs >= 60
                    ? `(${Math.floor(secs / 60)}m ${secs % 60}s)`
                    : `(${secs}s)`;
                // After 30 s a still-counting timer is suspicious — surface
                // a hint so the user knows the daemon should be looked at.
                if (secs === 30) span.classList.add("mv-wait-slow");
                // …and at 45 s, stop counting and SAY something. A counter
                // that runs forever is not information: the fetch is not
                // "still working", it has failed in a way nothing reports —
                // e.g. a Gmail row indexed over IMAP (small sequential uid)
                // whose body the Gmail API path cannot address at all,
                // because it only knows uids derived from Gmail message ids
                // (Bob 2026-08-09: "definitely need an appropriate message
                // rather than an infinitely counting timer"). Whatever the
                // cause, at this point the honest thing is to say the body
                // did not arrive, keep the snippet visible, and offer the
                // paths that DO work.
                if (secs >= WAIT_GIVE_UP_SECS) {
                    clearInterval(tick);
                    off?.();
                    renderBodyFetchGaveUp(bodyEl, accountId, uid, folderId, secs, previewText, specialUse, envelope || cached || undefined);
                }
            }, 1000);
            // Subscribe via Store bus (the post-refactor surface). The
            // reconciler publishes `bodyAvailable` on `message:<uuid>` (or
            // `folder:<id>` when uuid isn't available); the wildcard catches
            // both. Match by account+uid because the viewer keys off those.
            // eslint-disable-next-line prefer-const — assigned below, referenced by the tick above
            let off: (() => void) | undefined;
            off = subscribeStore("*", (ev: any) => {
                if (ev.kind !== "bodyAvailable") return;
                if (ev.accountId !== accountId || ev.uid !== uid) return;
                if (captureGen !== showMessageGeneration) { off(); clearInterval(tick); return; }
                off();
                clearInterval(tick);
                showMessage(accountId, uid, folderId, specialUse, true, envelope || cached || undefined).catch(() => { /* */ });
            });
            return;
        }
        // Web/Android service path: a failed body fetch comes back as
        // bodyError on the message itself (the desktop daemon signals
        // cached=false + a bodyAvailable event instead). Render it as a
        // fetch failure — NOT the "no body text — only a subject"
        // placeholder, which claims the message is empty when it isn't
        // (Bob 2026-07-03) — and do NOT parsedCachePut, so reopening the
        // message retries instead of pinning the failure in cache.
        if ((msg as any).bodyError && !(msg as any).bodyHtml && !(msg as any).bodyText) {
            try { renderHeaderFromEnvelope(headerEl, msg as any); headerEl.hidden = false; } catch { /* */ }
            const transient = (msg as any).bodyErrorTransient;
            bodyEl.innerHTML = `<div class="mv-system-message mv-system-error">
                <div class="mv-system-tag">mailx</div>
                <div class="mv-system-title">Body fetch failed</div>
                <div class="mv-system-body">${escapeHtml((msg as any).bodyError)}<br><span style="color:var(--color-text-muted);font-size:0.9em">${transient ? "Reopen this message to retry." : "The server reports this message no longer exists (deleted or moved by another client)."}</span></div>
            </div>`;
            currentMessage = msg;
            currentAccountId = accountId;
            return;
        }
        // Cache the fully-parsed result so re-clicking this message paints
        // synchronously next time (no IPC, no parse). Cache is bounded
        // (PARSED_CACHE_LIMIT) and skipped if we got here via cache hit.
        if (!cachedMsg) parsedCachePut(accountId, folderId, uid, msg);
        currentMessage = msg;
        currentAccountId = accountId;

        // Mark as read — gated by user prefs:
        //   - mailx-automark-read (default "true"): if "false", never auto-mark
        //   - mailx-automark-delay (default "2"): seconds to wait before
        //     marking. Lets the user click through messages quickly without
        //     marking ones they didn't actually read. The timer is tied to
        //     showMessageGeneration; navigating to another message advances
        //     the generation and cancels the pending mark.
        if (!seenOf(msg)) {
            let enabled = true;
            let delaySec = 2;
            try {
                enabled = localStorage.getItem("mailx-automark-read") !== "false";
                const d = parseFloat(localStorage.getItem("mailx-automark-delay") || "2");
                if (Number.isFinite(d) && d >= 0) delaySec = d;
            } catch { /* private mode — defaults */ }
            if (enabled) {
                const captureGen = gen;
                const apply = () => {
                    // Stale: user moved on before the timer fired.
                    if (captureGen !== showMessageGeneration) return;
                    setSeen(msg, true);
                    updateFlags(accountId, uid, msg.flags, (msg as any).folderId);
                    // Mirror to message-state + row so list display flips.
                    try { stateUpdateFlags(accountId, uid, msg.flags, (msg as any).folderId); } catch { /* */ }
                    try { setRowSeen(accountId, uid, true, (msg as any).folderId); } catch { /* */ }
                };
                if (delaySec === 0) apply();
                else setTimeout(apply, delaySec * 1000);
            }
        }

        // Header
        headerEl.hidden = false;
        const fromEl = headerEl.querySelector(".mv-from")!;
        const toEl = headerEl.querySelector(".mv-to")!;
        fromEl.textContent = formatAddr(msg.from);
        setRecipientLine(toEl, msg.to, msg.cc, msg.deliveredTo);
        headerEl.querySelector(".mv-subject")!.textContent = msg.subject;

        // Calendar invite card. Lives BETWEEN the header and the body as its
        // own element, not inside .mv-body — the body's innerHTML is replaced
        // wholesale on every render, which would take the card with it.
        // Removed and rebuilt per message so a non-invite never inherits the
        // previous one (the "viewer must clear" rule).
        document.getElementById("mv-invite")?.remove();
        try {
            const card = buildInviteCard((msg as any).invite, String(msg.bodyHtml || ""));
            if (card) {
                card.id = "mv-invite";
                headerEl.insertAdjacentElement("afterend", card);
            }
        } catch (e: any) {
            console.warn("[viewer] invite card failed:", e?.message || e);
        }
        document.dispatchEvent(new CustomEvent("mailx-message-shown", { detail: { accountId } }));

        // Right-click on email addresses in header: copy name, copy address,
        // copy both, add to contacts, plus reply actions for the whole message.
        for (const el of [fromEl, toEl]) {
            el.addEventListener("contextmenu", (e: Event) => {
                e.preventDefault();
                const me = e as MouseEvent;
                const items: MenuItem[] = [];
                const allAddrs = el === fromEl ? [msg.from] : [...(msg.to || []), ...(msg.cc || [])];
                // Bulk mail can address hundreds of people; enumerating every
                // one builds a menu thousands of items tall. Show the first
                // few and say how many were left out.
                const addrs = allAddrs.slice(0, CONTEXT_MENU_ADDR_CAP);
                const omitted = allAddrs.length - addrs.length;
                for (const addr of addrs) {
                    if (!addr?.address) continue;
                    const name = addr.name || "";
                    const both = name ? `${name} <${addr.address}>` : addr.address;
                    if (name) {
                        items.push({ label: `Copy name: ${name}`, action: () => navigator.clipboard.writeText(name) });
                    }
                    items.push({ label: `Copy address: ${addr.address}`, action: () => navigator.clipboard.writeText(addr.address) });
                    if (name) {
                        items.push({ label: `Copy both: ${both}`, action: () => navigator.clipboard.writeText(both) });
                    }
                    items.push({
                        label: `Open in contacts: ${addr.address}`,
                        action: async () => {
                            const { openAddressBook } = await import("./address-book.js");
                            await openAddressBook(addr.address);
                        },
                    });
                    items.push({
                        label: `Add to contacts: ${addr.address}`,
                        action: async () => {
                            await showAddContactDialog(name, addr.address);
                        },
                    });
                    // "Add to preferred" — separate path: writes to
                    // contacts.jsonc#preferred[] with an optional source tag.
                    // Distinct from "Add to contacts" which goes into the DB +
                    // pushes to Google. Preferred entries rank higher in
                    // autocomplete and survive Google sync's churn.
                    items.push({
                        label: `Add to preferred: ${addr.address}`,
                        action: async () => {
                            const tag = prompt("Tag (e.g. work, family, vendor) — leave blank for default:", "");
                            if (tag === null) return; // user cancelled
                            try {
                                await addPreferredContact({ name, email: addr.address, source: tag.trim() || undefined });
                                const status = document.getElementById("status-sync");
                                if (status) status.textContent = `Added to preferred: ${addr.address}${tag ? ` [${tag}]` : ""}`;
                            } catch (e: any) {
                                alert(`Couldn't add to preferred: ${e?.message || e}`);
                            }
                        },
                    });
                    // "Never use this address" — adds to contacts.jsonc
                    // denylist[]: hidden from autocomplete everywhere. The
                    // long-standing gap from Q149(2) — the affordance existed
                    // in compose autocomplete rows (⊘) but not here.
                    items.push({
                        label: `⊘ Never use this address: ${addr.address}`,
                        action: async () => {
                            try {
                                const { addToDenylist } = await import("../lib/api-client.js");
                                await addToDenylist(addr.address);
                                const status = document.getElementById("status-sync");
                                if (status) status.textContent = `Denylisted: ${addr.address}`;
                            } catch (e: any) {
                                alert(`Couldn't denylist: ${e?.message || e}`);
                            }
                        },
                    });
                    // Mark / unmark sender as priority — visual highlight in
                    // the message list (gold left bar + bold sender name);
                    // strongest while unread, fades when read. Stored as
                    // `priority: true` on the contacts.jsonc preferred[]
                    // entry (auto-creates the entry if it doesn't exist).
                    items.push({
                        label: `★ Mark sender as priority: ${addr.address}`,
                        action: async () => {
                            try {
                                const { setPrioritySender } = await import("../lib/api-client.js");
                                await setPrioritySender(addr.address, true, name);
                                const { refreshPriorityIndex } = await import("./message-list.js");
                                await refreshPriorityIndex();
                                const status = document.getElementById("status-sync");
                                if (status) status.textContent = `Priority: ${addr.address}`;
                            } catch (e: any) {
                                alert(`Couldn't mark priority: ${e?.message || e}`);
                            }
                        },
                    });
                    items.push({ label: "", action: () => {}, separator: true });
                }
                // "Mark as valid" — the display-name warning is only useful if
                // it can be silenced per sender. Reuses the shared allowlist
                // (same senders/domains that gate remote images), so one act of
                // trust covers both (Bob 2026-08-20: "another variation on
                // approved senders. If a message is flagged I should be able to
                // mark it as valid").
                if (el === fromEl && msg.from?.address) {
                    const addr = String(msg.from.address);
                    const dom = addr.split("@").pop() || "";
                    const claimed = displayNameClaimsOtherAddress(String(msg.from.name || ""), addr);
                    if (claimed && !isSenderApproved(addr)) {
                        const approve = async (type: "sender" | "domain", value: string) => {
                            noteApprovedLocally(type, value);          // optimistic — row updates now
                            try {
                                await allowRemoteContent(type, value);
                                await refreshApprovedSenders();
                                document.dispatchEvent(new CustomEvent("mailx-allowlist-changed"));
                                const st = document.getElementById("status-sync");
                                if (st) st.textContent = `Marked valid: ${value}`;
                            } catch (e: any) {
                                const st = document.getElementById("status-sync");
                                if (st) {
                                    st.textContent = `Couldn't save "${value}" to the allowlist — ${e?.message || e}`;
                                    st.style.color = "oklch(0.65 0.2 25)";
                                }
                                console.error("[allowlist] approve failed:", e);
                            }
                        };
                        items.push({ label: `✓ Mark ${addr} as a valid sender`, action: () => void approve("sender", addr) });
                        if (dom) items.push({ label: `✓ Trust all senders at ${dom}`, action: () => void approve("domain", dom) });
                        items.push({ label: "", action: () => {}, separator: true });
                    }
                    // "Trust the ‹list› mailing list" (Bob 2026-09-09: "how do I
                    // say that this is a valid mailing list message"). A list
                    // that keeps the poster's From — MIT's Mailman, for a Gmail
                    // author — leaves nothing per-sender to trust: the author
                    // is unproved on every post, by the list's own doing. The
                    // LIST is proved (its host passed SPF for the bounce
                    // address), so the list is what the reader vouches for.
                    // Offered from the chip's own advice, which is the only
                    // thing an unflagged list post shows; the banner has its
                    // own button.
                    //
                    // Offered on every proved post, trusted or not: the write
                    // is a toggle, and once the list is trusted the chip and
                    // banner are gone, so this menu is the only place an
                    // approval can be undone without editing allowlist.jsonc
                    // by hand (Bob 2026-09-09: "how would I undo an approval").
                    const listSpam = (msg as any).spamScore as { listId?: string; listProved?: boolean; listTrusted?: boolean };
                    if (listSpam?.listProved && listSpam.listId) {
                        const listId = listSpam.listId;
                        const untrust = !!listSpam.listTrusted;
                        items.push({
                            label: untrust ? `✗ Stop trusting the ${listId} mailing list` : `✓ Trust the ${listId} mailing list`,
                            action: async () => {
                                const st = document.getElementById("status-sync");
                                try {
                                    // A toggle: the answer says which way it went,
                                    // and it must match what was asked for — a
                                    // mismatch means the write did not land.
                                    const res = await trustSenderOrDomain("list", listId);
                                    if (!res || !!res.trusted === untrust) throw new Error("the list did not save");
                                    listSpam.listTrusted = res.trusted;
                                    if (res.trusted) {
                                        document.querySelector(".mv-spamscore")?.remove();
                                        document.querySelector(".mv-spamscore-action")?.remove();
                                        document.querySelector(".mv-trust")?.remove();
                                    }
                                    if (st) st.textContent = res.trusted
                                        ? `Trusting the ${listId} mailing list`
                                        : `No longer trusting the ${listId} mailing list — reopen the message to see its score again`;
                                } catch (e: any) {
                                    // The reader chose an action and must know whether it took.
                                    if (st) {
                                        st.textContent = `Couldn't trust the ${listId} list — ${e?.message || e}`;
                                        st.style.color = "oklch(0.65 0.2 25)";
                                    }
                                    console.error("[allowlist] trust list failed:", e);
                                }
                            },
                        });
                        items.push({ label: "", action: () => {}, separator: true });
                    }
                }
                if (omitted > 0) {
                    items.push({
                        label: `… ${omitted} more recipients — copy the whole list`,
                        action: () => navigator.clipboard.writeText(allAddrs.map(formatAddr).join(", ")),
                    });
                    items.push({ label: "", action: () => {}, separator: true });
                }
                items.push({ label: "Reply", action: () => document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "reply" } })) });
                items.push({ label: "Reply All", action: () => document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "replyAll" } })) });
                items.push({ label: "Forward", action: () => document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "forward" } })) });
                // Viewer header text IS selectable — a Copy has to be here too.
                showContextMenu(me.clientX, me.clientY, items, { editTarget: me.target });
            });
        }
        headerEl.querySelector(".mv-date")!.textContent = new Date(msg.date).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

        // Unsubscribe button (upper right of header).
        //   - One-Click (RFC 8058): POST via service; show result in status bar.
        //   - Plain HTTPS URL: open externally for user confirmation.
        //   - mailto: open a pre-filled compose so the reply uses the right account.
        const unsubBtn = document.getElementById("mv-unsubscribe") as HTMLAnchorElement;
        const httpUrl = (msg as any).listUnsubscribeHttp || "";
        const mailUrl = (msg as any).listUnsubscribeMail || "";
        const oneClick = !!(msg as any).listUnsubscribeOneClick;
        const anyUrl = httpUrl || mailUrl || msg.listUnsubscribe || "";
        if (unsubBtn) {
            if (anyUrl) {
                unsubBtn.hidden = false;
                unsubBtn.textContent = "Unsubscribe";
                unsubBtn.removeAttribute("title");
                unsubBtn.href = httpUrl || mailUrl || "#";
                unsubBtn.onclick = async (e) => {
                    e.preventDefault();
                    const status = document.getElementById("status-sync");
                    if (httpUrl && oneClick) {
                        if (status) status.textContent = "Unsubscribing…";
                        try {
                            const result = await unsubscribeOneClick(httpUrl);
                            if (status) {
                                status.textContent = result.ok
                                    ? `Unsubscribed (${result.status} ${result.statusText})`
                                    : `Unsubscribe failed: ${result.status} ${result.statusText}`;
                            }
                        } catch (err: any) {
                            if (status) status.textContent = `Unsubscribe error: ${err?.message || err}`;
                        }
                        return;
                    }
                    if (httpUrl) {
                        const api = (window as any).mailxapi;
                        if (api?.openExternal) api.openExternal(httpUrl);
                        else window.open(httpUrl, "_blank", "noopener,noreferrer");
                        return;
                    }
                    if (mailUrl) {
                        const m = mailUrl.match(/^mailto:([^?]*)(?:\?(.*))?$/i);
                        const to = m?.[1] ? decodeURIComponent(m[1]) : "";
                        const qs = new URLSearchParams(m?.[2] || "");
                        // 2026-05-13: when the unsubscribe-side specifies no
                        // subject/body in the mailto, pre-fill from the
                        // ORIGINAL message so the human (or filter) at the
                        // other end sees what list this is for. Bare
                        // mailto:unsub+<token>@... gives the recipient an
                        // opaque token and no human-readable context.
                        const origFrom = msg.from
                            ? (msg.from.name ? `${msg.from.name} <${msg.from.address}>` : msg.from.address)
                            : "";
                        const origSubject = msg.subject || "";
                        const origDate = msg.date
                            ? new Date(msg.date).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
                            : "";
                        const subject = qs.get("subject")
                            || (origSubject ? `Unsubscribe: ${origSubject}` : "Unsubscribe");
                        const inlineBody = qs.get("body") || "";
                        const escape = (s: string) => s
                            .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
                        const contextLines: string[] = [];
                        if (origFrom)    contextLines.push(`From: ${escape(origFrom)}`);
                        if (origSubject) contextLines.push(`Subject: ${escape(origSubject)}`);
                        if (origDate)    contextLines.push(`Date: ${escape(origDate)}`);
                        const contextBlock = contextLines.length
                            ? `<p>—<br>${contextLines.join("<br>")}</p>`
                            : "";
                        const bodyHtml = inlineBody
                            ? `<p>${escape(inlineBody)}</p>${contextBlock}`
                            : `<p>Please unsubscribe me from this list.</p>${contextBlock}`;
                        const init = {
                            mode: "new",
                            accountId: currentAccountId,
                            to: to ? [{ name: "", address: to }] : [] as { name: string; address: string }[],
                            cc: [] as { name: string; address: string }[],
                            subject,
                            bodyHtml,
                            inReplyTo: "",
                            references: [] as string[],
                            accounts: [] as { id: string; name: string; email: string }[],
                        };
                        sessionStorage.setItem("composeInit", JSON.stringify(init));
                        document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "new" } }));
                    }
                };
            } else {
                unsubBtn.hidden = true;
            }
        }

        // View Thread button — opens the thread popup from the message list
        // so the user can see all messages in the conversation. Works from
        // the viewer even when thread-grouping is off.
        const threadBtn = document.getElementById("mv-view-thread") as HTMLButtonElement;
        if (threadBtn) {
            const tid = (msg as any).threadId || "";
            if (tid) {
                threadBtn.hidden = false;
                threadBtn.onclick = async () => {
                    const { showThreadPopup } = await import("./message-list.js");
                    await showThreadPopup(threadBtn, { accountId, threadId: tid });
                };
            } else {
                threadBtn.hidden = true;
            }
        }

        // View Source button. CRITICAL: handler reads from the
        // `currentMessage` singleton, NOT a captured `msg` closure. If the
        // user clicks while a different message is mid-render the singleton
        // is null (cleared at top of showMessage) so the click is a no-op
        // — instead of returning the previous message's path. The principle
        // (per Bob): there is one source-of-truth identity for "what's
        // shown" and every action reads from it at click time.
        const srcBtn = document.getElementById("mv-view-source") as HTMLButtonElement;
        if (srcBtn) {
            if (msg.emlPath) {
                srcBtn.hidden = false;
                srcBtn.title = `${msg.emlPath}\n(click to copy path, double-click to open in default text editor)`;
                srcBtn.onclick = () => {
                    const path = currentMessage?.emlPath;
                    if (!path) return;
                    navigator.clipboard.writeText(path).then(() => {
                        const status = document.getElementById("status-sync");
                        if (status) status.textContent = `Path copied: ${path}`;
                    }).catch(() => {
                        prompt("EML file path:", path);
                    });
                };
                // Double-click opens the .eml in the OS default *text*
                // editor (Notepad / TextEdit / $EDITOR). Distinct from
                // the file's associated app, which for .eml is usually
                // Outlook — not what the user wants when inspecting the
                // raw MIME. Bob 2026-05-11: "if anything it should open
                // up the default .txt editor."
                srcBtn.ondblclick = (e) => {
                    e.preventDefault();
                    const path = currentMessage?.emlPath;
                    if (!path) return;
                    import("../lib/api-client.js").then(m => m.openInTextEditor(path)).then((r: any) => {
                        const status = document.getElementById("status-sync");
                        if (status) {
                            status.textContent = r?.ok
                                ? `Opened in ${r.opener}: ${path}`
                                : `Open failed: ${r?.reason || "unknown"}`;
                        }
                    }).catch((e: any) => {
                        console.error("[mv] openInTextEditor failed:", e);
                    });
                };
            } else {
                srcBtn.hidden = true;
                srcBtn.onclick = null;
                srcBtn.ondblclick = null;
            }
        }

        // Drafts and Outbox messages open directly into compose. The Edit
        // Draft button stays as a fallback (and to keep the manual flow
        // discoverable), but selecting a draft no longer requires the user
        // to click it — opening a draft and editing it are the same action.
        // Guard: don't re-open compose for the SAME draft on every viewer
        // re-render (msg-state events fire on count updates etc.). And
        // don't auto-open while a compose overlay is already up — the user
        // is mid-edit on something and shouldn't lose focus to a new window.
        const editBtn = document.getElementById("mv-edit-draft") as HTMLButtonElement;
        // Detect drafts by FLAG, not just folder. A message marked \Draft
        // by the server is editable regardless of which folder the user
        // is currently viewing (e.g., clicking a draft from a thread in
        // INBOX, or from "All Inboxes" where currentSpecialUse is empty).
        // Bob 2026-05-12: "How do I get into edit mode?"
        const msgFlags: string[] = Array.isArray(msg?.flags) ? msg.flags : [];
        const flagged = msgFlags.includes("\\Draft");
        const isDraft = flagged || specialUse === "drafts" || specialUse === "outbox";
        const openDraftInCompose = (cm: any) => {
            const init = {
                mode: "draft",
                accountId: currentAccountId,
                // Keep the draft's From identity (reply-to-alias) on reopen —
                // without this the compose From reset to the account default.
                fromRaw: cm.from?.address
                    ? (cm.from.name ? `${cm.from.name} <${cm.from.address}>` : cm.from.address)
                    : "",
                to: cm.to || [],
                cc: cm.cc || [],
                subject: cm.subject || "",
                bodyHtml: cm.bodyHtml || "",
                inReplyTo: cm.inReplyTo || "",
                references: cm.references || [],
                accounts: [] as { id: string; name: string; email: string }[],
                draftUid: cm.uid,
                draftFolderId: cm.folderId,
                draftId: cm.mailxDraftId || "",
            };
            sessionStorage.setItem("composeInit", JSON.stringify(init));
            document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "draft" } }));
        };
        if (editBtn) {
            if (isDraft) {
                editBtn.hidden = false;
                editBtn.textContent = specialUse === "outbox" ? "Edit & Send" : "Edit Draft";
                editBtn.onclick = () => { const cm = currentMessage; if (cm) openDraftInCompose(cm); };
            } else {
                editBtn.hidden = true;
                editBtn.onclick = null;
            }
        }
        // Drafts render in the preview pane like any other message. The
        // user opens them for editing explicitly via the "Edit Draft"
        // button or by double-clicking the row (Bob 2026-05-23: "if I look
        // at a letter in drafts it creates popup immediately. It shouldn't
        // do that unless I ask — I should just see the preview").

        // Details toggle — show extra headers (Delivered-To, Return-Path, Message-ID, etc.)
        const detailsEl = document.getElementById("mv-details") as HTMLElement;
        const detailsBtn = document.getElementById("mv-toggle-details") as HTMLButtonElement;
        if (detailsEl && detailsBtn) {
            // Q56: every row gets a Copy button so paths / IDs can be pasted
            // into accounts.jsonc hints or bug reports.
            const row = (label: string, value: string) =>
                `<div class="mv-details-row"><span class="mv-details-label">${label}:</span> <span class="mv-details-value">${escapeText(value)}</span> <button type="button" class="mv-details-copy" data-copy="${escapeText(value).replace(/"/g, "&quot;")}" title="Copy">⧉</button></div>`;
            const lines: string[] = [];
            if (msg.deliveredTo) lines.push(row("Delivered-To", msg.deliveredTo));
            if (msg.returnPath) lines.push(row("Return-Path", msg.returnPath));
            if (msg.messageId) lines.push(row("Message-ID", msg.messageId));
            if (msg.listUnsubscribe) lines.push(row("Unsubscribe", msg.listUnsubscribe));
            if (msg.emlPath) lines.push(row("EML file", msg.emlPath));
            lines.push(row("Account", accountId));
            lines.push(row("UID", `${msg.uid} (folder ${msg.folderId})`));
            detailsEl.innerHTML = lines.join("");
            detailsEl.hidden = true;
            detailsBtn.textContent = "Details";
            detailsBtn.onclick = () => {
                detailsEl.hidden = !detailsEl.hidden;
                detailsBtn.textContent = detailsEl.hidden ? "Details" : "\u2713 Details";
            };
            // Wire copy buttons.
            detailsEl.querySelectorAll<HTMLButtonElement>(".mv-details-copy").forEach(btn => {
                btn.addEventListener("click", async (e) => {
                    e.stopPropagation();
                    const val = btn.dataset.copy || "";
                    try {
                        await navigator.clipboard.writeText(val);
                        btn.textContent = "✓";
                        setTimeout(() => { btn.textContent = "⧉"; }, 1500);
                    } catch {
                        prompt("Copy:", val);
                    }
                });
            });
        }

        // Forgery evidence — its OWN banner, not part of the remote-content
        // one, because the two are independent. The 2026-08-25 phishes embedded
        // their card as a cid: image, so nothing was remote, the remote banner
        // never rendered, and the message displayed with no warning of any kind
        // even though the server had already marked it spam.
        //
        // Always visible, never collapsed: this is not a preference, and a
        // reader who has to expand a dropdown to find out the message is forged
        // has already been asked to trust it. Findings name their evidence so
        // it can be checked rather than believed. (Claude Code 2026-08-25)
        bodyEl.innerHTML = "";

        // The server's spam score — shown ONLY when it is high enough to mean
        // something (Bob 2026-08-25: "only show the spam score if it is high
        // and likely"). The first cut put a chip on every message that carried
        // a score, which is nearly all of them: a badge that is always present
        // carries no information and becomes furniture the eye stops reading.
        //
        // The floor is halfway to the SERVER'S OWN threshold, so it is a
        // proportion rather than a constant and follows if that threshold ever
        // changes. Halfway is where "did not quite trip the wire" starts being
        // a different statement from "clean" — the sextortion mail that
        // prompted all of this scored 2.7 against 4.5 and was delivered
        // unflagged, so showing only the flagged ones would miss exactly the
        // case worth showing.
        //
        // The COLOUR comes from what the rules that scored are evidence of, not
        // from the number (Claude Code 2026-08-27): a newsletter DMARC proved,
        // or one whose whole score is bulk-mail rules, gets the neutral chip
        // however high it climbed. Blue Orchestra scored 10.9 on a Spamhaus
        // listing of a linked advocacy site and a bulk-mail hit; painting that
        // the same red as a forged sender is what teaches a reader to ignore
        // the chip. The floor stays at half the server's own threshold —
        // see docs/spam-false-positives.md on why tuning it is not the fix.
        const spam = (msg as any).spamScore as {
            score: number; threshold: number; flagged: boolean;
            kind?: string; proved?: boolean; provedBy?: string; bulkOnly?: boolean;
            reasons?: string[]; trusted?: boolean;
            listId?: string; listProved?: boolean; listTrusted?: boolean;
        };
        // A sender the reader has approved, whose identity is proved: the chip
        // goes with the banner. Repeating the score after the reader has said
        // "this correspondent is fine" is what turns an indicator into
        // furniture. (Bob 2026-08-28)
        if (spam && !spam.trusted && Number.isFinite(spam.score) && (spam.flagged || spam.score >= spam.threshold / 2)) {
            const benign = !!spam.proved || spam.kind === "bulk";
            const level = benign ? "bulk" : spam.flagged ? "high" : "mid";
            const chip = document.createElement("div");
            chip.className = `mv-spamscore mv-spamscore-${level}`;
            const why = (spam.reasons || []).length ? "\n\n" + spam.reasons.join("\n") : "";
            // Name the proof the analysis actually used. "Passed DMARC" on a
            // message with no Authentication-Results at all is a claim the
            // reader cannot check — Bob's own mail, 2026-08-27.
            const provedNote = spam.provedBy === "dkim"
                ? "the sending domain signed it and the signature verified"
                : spam.provedBy === "trusted"
                    ? "it never passed through an untrusted host"
                    : "the sending domain passed DMARC";
            // "Every rule that scored is a bulk-mail test" is a claim about ALL
            // of them, and the reader can check it against the list printed
            // directly below. So it is said only when `bulkOnly` says it is
            // true — never inferred from the sender being proved, which is a
            // fact about the headers and none about the rules. The Altis
            // Hotels welcome mail, 2026-09-03 (Bob: "marked as spam but is
            // valid"): DMARC-proved, and 3.2 of its 8.3 was KAM_ACCOUNTPHISH,
            // named in the very next line of the same tooltip.
            chip.title = (level === "bulk"
                ? (spam.proved
                    ? spam.bulkOnly
                        ? `Scored by bulk-mail or blocklist rules; ${provedNote}, so the sender is proved.`
                        : `${provedNote[0].toUpperCase()}${provedNote.slice(1)}, so the sender is proved. Not every rule that scored is a bulk-mail test — they are listed below.`
                    : spam.bulkOnly
                        ? "Every rule that scored is a bulk-mail test — none of them test who sent this."
                        : "No forgery rule scored. The rules that did are listed below.")
                : level === "high"
                    ? "Your mail server scored this at or above its spam threshold and flagged it."
                    : "Below your server's spam threshold, but more than halfway to it.") + why
                // The list that provably delivered this, and where the action
                // lives — the chip is the only thing an unflagged list post
                // shows, so it has to say how to make it stop (2026-09-09).
                + (spam.listProved && spam.listId && !spam.listTrusted
                    ? `\n\nDelivered by the ${spam.listId} mailing list, which your server proved sent it. Right-click the From address to trust the list.`
                    : "");
            chip.textContent = `spam score ${spam.score} of ${spam.threshold}`;
            bodyEl.appendChild(chip);
            // The action beside the warning it answers (Bob 2026-09-09: "it
            // would be nice to have an [approve this list] on the red warning
            // banner"). An unflagged list post has no banner — the chip IS
            // the warning — so the button sits next to it. Same write as the
            // From-menu item and the banner button: trustedLists by List-Id.
            if (spam.listProved && spam.listId && !spam.listTrusted) {
                const listId = spam.listId;
                const b = document.createElement("button");
                b.className = "mv-trust-action mv-spamscore-action";
                b.textContent = `Trust the ${listId} list`;
                b.title = `Delivered by the ${listId} mailing list, which your server proved sent it. Trusting the list hides this score on every post the list delivers.`;
                b.addEventListener("click", async () => {
                    b.disabled = true;
                    try {
                        // Only ever ADDS, so anything but `trusted: true` means
                        // the write did not land — never hide the chip on a
                        // failed save.
                        const res = await trustSenderOrDomain("list", listId);
                        if (!res?.trusted) throw new Error("the list did not save");
                        spam.listTrusted = true;
                        chip.remove();
                        b.remove();
                        document.querySelector(".mv-trust")?.remove();
                    } catch (e: any) {
                        // The reader pressed a button and must know whether it took.
                        b.disabled = false;
                        b.textContent = `Could not save: ${e?.message || e}`;
                    }
                });
                bodyEl.appendChild(b);
            }
        }

        const trust = ((msg as any).trust || []) as { id: string; severity: string; text: string; detail: string; via?: string; list?: string; senderVia?: string; sender?: string }[];
        if (trust.length) {
            // An `info` finding is not an accusation, so it gets neither the
            // headline nor the "do not open anything" footer — it renders as a
            // plain note in the register of the remote-content banner. A single
            // danger or caution finding anywhere in the set still sets the
            // whole banner, because the worst evidence is the one that matters.
            // (Claude Code 2026-08-27)
            const worst = trust.some(f => f.severity === "danger") ? "danger"
                : trust.some(f => f.severity === "caution") ? "caution" : "info";
            const head = worst === "danger" ? "This message is not what it says it is"
                : worst === "caution" ? "Check this message before acting on it" : "";
            const warn = document.createElement("div");
            warn.className = `mv-trust mv-trust-${worst}`;
            warn.innerHTML =
                (head ? `<div class="mv-trust-head">${head}</div>` : "") +
                trust.map(f =>
                    `<div class="mv-trust-item mv-trust-${escapeText(f.severity)}">` +
                        `<div class="mv-trust-text">${escapeText(f.text)}</div>` +
                        `<div class="mv-trust-detail">${escapeText(f.detail)}</div>` +
                    `</div>`).join("") +
                (worst === "info" ? ""
                    : `<div class="mv-trust-foot">Links and attachments here should not be opened.</div>`);

            // "Stop warning me about this sender" — the action the note was
            // missing. Bob 2026-08-28: "I don't need the spam reminder but I
            // don't want to say show remote content in order to prevent
            // tracking." Until now the only way to vouch for a sender was the
            // remote-content banner, which turns on their tracking pixels; this
            // writes the separate trustedSenders/trustedDomains list instead.
            //
            // Offered only where the server's own verdict is the whole finding
            // and the sender is proved. A forgery banner has no such button: no
            // list should be able to silence "this message is not what it says
            // it is", and there is nothing to trust when the identity is the
            // thing in doubt.
            // Offered whenever trusting the sender would clear EVERYTHING on
            // the banner: the server's verdict, and since 2026-09-11 the
            // redirector warning too (a proved sender vouches for its own
            // links). Any other finding present, or a danger, and there is no
            // button — no list silences those. The Toast receipt carried
            // both a spam verdict and a redirector warning, so the old
            // "exactly one finding" rule hid the button on the one message
            // that needed it (Bob: "there should be a trust button").
            const trustClearable = worst !== "danger"
                && trust.every(f => f.id === "server-spam-verdict" || f.id === "redirector-link");
            const senderProved = !!spam?.proved || trust.some(f => !!f.sender);
            const fromAddr = (msg.from?.address || "").toLowerCase();
            const fromDomain = fromAddr.split("@")[1] || "";
            if (trustClearable && senderProved && fromAddr) {
                const actions = document.createElement("div");
                actions.className = "mv-trust-actions";
                // Name the sender in the label — a button that says "trust" with
                // no object tells the reader nothing they can verify later.
                for (const [label, type, value] of [
                    [`Stop warning about ${fromAddr}`, "sender", fromAddr],
                    [`Stop warning about *@${fromDomain}`, "domain", fromDomain],
                ] as const) {
                    if (!value) continue;
                    const b = document.createElement("button");
                    b.className = "mv-trust-action";
                    b.textContent = label;
                    b.addEventListener("click", async () => {
                        b.disabled = true;
                        try {
                            // The button only ever ADDS, so anything but
                            // `trusted: true` means the write did not land —
                            // a missing bridge method, a merge that dropped
                            // the list (2026-08-28), a refused cloud write.
                            // Removing the banner on a failed save is the
                            // worst outcome: it looks saved and comes back.
                            const res = await trustSenderOrDomain(type, value);
                            if (!res?.trusted) throw new Error("the sender list did not save");
                            warn.remove();
                            document.querySelector(".mv-spamscore")?.remove();
                        } catch (e: any) {
                            // Never fail silently: the reader pressed a button
                            // and must know whether it took.
                            b.disabled = false;
                            b.textContent = `Could not save: ${e?.message || e}`;
                        }
                    });
                    actions.appendChild(b);
                }
                warn.appendChild(actions);
            }
            // "Trust the ‹list› list" (Bob 2026-09-09: "how do I say that this
            // is a valid mailing list message"). A finding carries `list` only
            // when the receiving server proved the list's host sent the
            // message and the list is not yet trusted; the entry it writes
            // (`trustedLists`, keyed on List-Id) counts only on such mail.
            // Not on a danger banner, for the same reason as every other
            // button here.
            const lists = worst === "danger" ? [] : Array.from(new Set(trust.map(f => f.list).filter(Boolean)));
            // A danger banner offers no button, but it must still SAY where
            // the list action is and that it would not help here — a red
            // banner with no way forward sends the reader hunting (Bob
            // 2026-09-09: "right mouse on the From … that's not obvious to
            // the normal user"). Every proved-list post that stays red is a
            // phishing/malware URL listing or an authentication failure,
            // which trusting the list never clears.
            const listSpam = (msg as any).spamScore as { listId?: string; listProved?: boolean; listTrusted?: boolean };
            if (worst === "danger" && listSpam?.listProved && listSpam.listId) {
                const note = document.createElement("div");
                note.className = "mv-trust-foot";
                note.textContent = listSpam.listTrusted
                    ? `Delivered by the ${listSpam.listId} mailing list, which you trust — that does not clear this finding.`
                    : `Delivered by the ${listSpam.listId} mailing list. Trusting the list (right-click the From address) would not clear this finding.`;
                warn.appendChild(note);
            }
            if (lists.length) {
                const actions = document.createElement("div");
                actions.className = "mv-trust-actions";
                for (const listId of lists) {
                    const b = document.createElement("button");
                    b.className = "mv-trust-action";
                    b.textContent = `Trust the ${listId} list`;
                    b.addEventListener("click", async () => {
                        b.disabled = true;
                        try {
                            const res = await trustSenderOrDomain("list", listId);
                            if (!res?.trusted) throw new Error("the list did not save");
                            warn.remove();
                            document.querySelector(".mv-spamscore")?.remove();
                        } catch (e: any) {
                            // The reader pressed a button and must know whether it took.
                            b.disabled = false;
                            b.textContent = `Could not save: ${e?.message || e}`;
                        }
                    });
                    actions.appendChild(b);
                }
                warn.appendChild(actions);
            }
            // "Trust ‹sender› via ‹relay›" (Bob 2026-09-10, Ground News: "another
            // valid site. How do I approve it?"). A finding carries senderVia
            // only when the reader already trusts the sender, nothing proves the
            // From line, and a relay DID sign the message; the entry it writes
            // counts only on mail from that sender that relay signs.
            const senderVias = worst === "danger" ? [] : Array.from(new Set(trust.map(f => f.senderVia).filter(Boolean)));
            if (senderVias.length) {
                const actions = document.createElement("div");
                actions.className = "mv-trust-actions";
                for (const pair of senderVias) {
                    const b = document.createElement("button");
                    b.className = "mv-trust-action";
                    b.textContent = `Trust ${pair}`;
                    b.title = `Adds "${pair}" to trustedSendersVia in allowlist.jsonc. Stops this note for mail from ${pair.split(" via ")[0]} that ${pair.split(" via ")[1]} signs; a message without that signature is still checked.`;
                    b.addEventListener("click", async () => {
                        b.disabled = true;
                        try {
                            const res = await trustSenderOrDomain("via", pair);
                            if (!res?.trusted) throw new Error("the sender-via list did not save");
                            warn.remove();
                            document.querySelector(".mv-spamscore")?.remove();
                        } catch (e: any) {
                            // The reader pressed a button and must know whether it took.
                            b.disabled = false;
                            b.textContent = `Could not save: ${e?.message || e}`;
                        }
                    });
                    actions.appendChild(b);
                }
                warn.appendChild(actions);
            }
            // "Trust ‹service› as an intermediary" (Bob 2026-09-08: "how do I
            // mark zoom and sites like that as trusted intermediaries?"). A
            // finding carries `via` only when a party PROVABLY handled the
            // message — the DKIM signer behind a google.com/url redirector,
            // the Sender: domain that signed a calendar invitation on the
            // organizer's behalf — so the button names a real party, and the
            // list entry it writes can only ever count on mail that party
            // signed. Nothing is offered on a danger banner: no list silences
            // "this message is not what it says it is".
            const vias = worst === "danger" ? [] : Array.from(new Set(trust.map(f => f.via).filter(Boolean)));
            if (vias.length) {
                const actions = document.createElement("div");
                actions.className = "mv-trust-actions";
                for (const via of vias) {
                    const b = document.createElement("button");
                    b.className = "mv-trust-action";
                    b.textContent = `Trust ${via} as an intermediary`;
                    b.addEventListener("click", async () => {
                        b.disabled = true;
                        try {
                            const res = await trustSenderOrDomain("intermediary", via);
                            if (!res?.trusted) throw new Error("the intermediary list did not save");
                            warn.remove();
                            document.querySelector(".mv-spamscore")?.remove();
                        } catch (e: any) {
                            // The reader pressed a button and must know whether it took.
                            b.disabled = false;
                            b.textContent = `Could not save: ${e?.message || e}`;
                        }
                    });
                    actions.appendChild(b);
                }
                warn.appendChild(actions);
            }
            bodyEl.appendChild(warn);
        }

        // Remote content banner (collapsible dropdown with sender/recipient details)
        if (msg.hasRemoteContent) {
            const senderAddr = msg.from?.address || "";
            const senderName = msg.from?.name || "";
            const senderDomain = senderAddr.split("@")[1] || "";
            const deliveredTo = msg.deliveredTo || "";
            const toAddr = msg.to?.[0]?.address || "";
            const returnPath = msg.returnPath || "";
            const isFlagged = !!(msg as any).isFlagged;
            const reputation = (msg as any).reputation as {
                flagged: boolean; listedCount: number; checkedCount: number;
                sources: Array<{ service: string; verdict: string }>;
                verdict: string; service: string;
                unavailable?: Array<{ service: string; reason: string }>;
            } | null;
            const reputationFlagged = !!(reputation && reputation.flagged);
            // Name the services that could NOT answer in the tooltip. A "1 of 1"
            // reads very differently once you know the other two refused the
            // query rather than cleared the domain.
            const reputationBlind = (reputation?.unavailable || [])
                .map(u => `${u.service}: ${u.reason}`).join("\n");
            const reputationText = reputationFlagged
                ? `⚠ ${reputation!.listedCount} of ${reputation!.checkedCount} reputation services flag <strong>${escapeText(senderDomain)}</strong> as <strong>${escapeText(reputation!.verdict)}</strong> (${escapeText(reputation!.sources.map(s => s.service).join(", "))})`
                : "";

            const banner = document.createElement("div");
            banner.className = "mv-remote-banner"
                + (isFlagged ? " mv-remote-banner-flagged" : "")
                + (reputationFlagged ? " mv-remote-banner-reputation" : "");
            banner.innerHTML =
                (isFlagged
                    ? `<div class="mv-rb-flagged">⚠ Sender on your watch list (you marked this sender or domain suspicious)</div>`
                    : "") +
                (reputationFlagged
                    ? `<div class="mv-rb-reputation"${reputationBlind ? ` title="Could not be checked —\n${escapeText(reputationBlind)}"` : ""}>${reputationText}</div>`
                    : "") +
                `<div class="mv-rb-summary">` +
                    `<span class="mv-rb-toggle">&#x25B8;</span>` +
                    `<span>Remote content blocked</span>` +
                    `<span class="mv-rb-buttons">` +
                        `<button id="btn-load-remote">Load once</button>` +
                        `<button id="btn-allow-sender" title="${escapeText(senderAddr)}">Always: ${escapeText(senderAddr)}</button>` +
                        (senderDomain ? `<button id="btn-allow-domain" title="*@${escapeText(senderDomain)}">Always: *@${escapeText(senderDomain)}</button>` : "") +
                    `</span>` +
                `</div>` +
                `<div class="mv-rb-details" hidden>` +
                    `<div class="mv-rb-info">` +
                        `<div><span class="mv-rb-label">From:</span> ${escapeText(senderName ? `${senderName} <${senderAddr}>` : senderAddr)}</div>` +
                        (deliveredTo ? `<div><span class="mv-rb-label">Delivered-To:</span> ${escapeText(deliveredTo)}</div>` : "") +
                        (toAddr && toAddr !== deliveredTo ? `<div><span class="mv-rb-label">To:</span> ${escapeText(toAddr)}</div>` : "") +
                        (returnPath && returnPath !== senderAddr ? `<div><span class="mv-rb-label">Return-Path:</span> ${escapeText(returnPath)}</div>` : "") +
                    `</div>` +
                    (deliveredTo || toAddr ? `<div class="mv-rb-actions"><button id="btn-allow-to">Always allow to: ${escapeText(deliveredTo || toAddr)}</button></div>` : "") +
                    `<div class="mv-rb-actions">` +
                        `<button id="btn-flag-sender" class="mv-rb-flag-btn" title="${escapeText(senderAddr)}">${isFlagged ? "Unwatch" : "Watch (mark suspicious)"} sender</button>` +
                        (senderDomain ? `<button id="btn-flag-domain" class="mv-rb-flag-btn" title="*@${escapeText(senderDomain)}">Watch domain *@${escapeText(senderDomain)}</button>` : "") +
                    `</div>` +
                    `<div class="mv-rb-actions"><button id="btn-edit-allowlist" title="View / edit the full allowlist">Edit allowlist…</button></div>` +
                `</div>`;
            bodyEl.appendChild(banner);

            // Toggle dropdown — click arrow or text to expand details
            const summary = banner.querySelector(".mv-rb-summary")!;
            const details = banner.querySelector(".mv-rb-details") as HTMLElement;
            const toggle = banner.querySelector(".mv-rb-toggle")!;
            summary.addEventListener("click", (e) => {
                if ((e.target as HTMLElement).tagName === "BUTTON") return;
                details.hidden = !details.hidden;
                toggle.textContent = details.hidden ? "\u25B8" : "\u25BE";
            });

            const loadRemote = async () => {
                banner.remove();
                const full = await getMessage(accountId, uid, true);
                if (full.bodyHtml) {
                    const oldIframe = bodyEl.querySelector("iframe");
                    if (oldIframe) oldIframe.remove();
                    const iframe = document.createElement("iframe");
                    iframe.srcdoc = wrapHtmlBody(full.bodyHtml, true);
                    bodyEl.appendChild(iframe);
                    installPreviewControls(iframe);
                }
                // Critical: refresh both `currentMessage` and the parsedCache
                // with the unblocked version. Otherwise any later showMessage
                // call (folder refresh, sync event, etc.) hits the stale
                // cached BLOCKED message, re-renders the banner, and the
                // user's click looks like it was forgotten — even though the
                // allowlist persist actually worked. Bob 2026-05-09:
                // "you added it to allowed but don't know it!"
                currentMessage = full;
                currentAccountId = accountId;
                parsedCachePut(accountId, folderId, uid, full);
                markSessionAllowed(accountId, uid);
            };

            banner.querySelector("#btn-load-remote")!.addEventListener("click", loadRemote);

            // Always-allow handlers: render the unblocked view IMMEDIATELY,
            // then persist the allowlist entry in the background. Pre-fix
            // these awaited the persist before re-rendering — if the persist
            // threw (GDrive mount unavailable, atomicWrite failure, IPC
            // error), the unhandled rejection silently aborted the handler
            // and the click looked like a no-op. Optimistic order matches
            // the rest of the local-first model: the user's action commits
            // visually, sync surfaces problems via the status bar.
            const persistAllow = async (type: "sender" | "domain" | "recipient", value: string): Promise<void> => {
                try {
                    await allowRemoteContent(type, value);
                } catch (e: any) {
                    const status = document.getElementById("status-sync");
                    if (status) {
                        status.textContent = `Allowlist save failed (${type}: ${value}) — ${e?.message || e}`;
                        status.style.color = "oklch(0.65 0.2 25)";
                    }
                    console.error(`[allowlist] ${type}=${value} save failed:`, e);
                }
            };

            banner.querySelector("#btn-allow-sender")?.addEventListener("click", () => {
                loadRemote();
                persistAllow("sender", senderAddr);
            });

            banner.querySelector("#btn-allow-domain")?.addEventListener("click", () => {
                loadRemote();
                persistAllow("domain", senderDomain);
            });

            banner.querySelector("#btn-allow-to")?.addEventListener("click", () => {
                const addr = deliveredTo || toAddr;
                if (!addr) return;
                loadRemote();
                persistAllow("recipient", addr);
            });

            // Watch (or unwatch) sender / domain — toggles the allowlist's
            // flaggedSenders / flaggedDomains lists (kept under the legacy
            // JSONC field names for backwards compat; UI now uses "watch /
            // suspicious" wording to disambiguate from the per-message ⚑/★
            // marker which means the OPPOSITE — "important to me"). Future
            // messages from this sender/domain show a ⚠ banner at the top
            // of the viewer. Note: marking a single sender is unreliable
            // because spammers rotate addresses — see TODO for rules.jsonc
            // which will support pattern-based matching as a primary tool.
            const onFlagToggle = async (type: "sender" | "domain", value: string) => {
                if (!value) return;
                try {
                    const result = await flagSenderOrDomain(type, value);
                    const status = document.getElementById("status-sync");
                    if (status) status.textContent = result.flagged
                        ? `Watching ${type}: ${value}`
                        : `Unwatched ${type}: ${value}`;
                    // Re-render this message so the banner picks up the new
                    // flagged state without the user having to reselect.
                    // `isRetry=true` bypasses the same-message guard at the
                    // top of showMessage — we genuinely want a fresh paint
                    // here, even though scroll position will reset (the
                    // user just clicked a control, so scroll loss is
                    // expected, not surprising).
                    if (currentMessage) {
                        invalidateParsedCache(currentAccountId, currentMessage.folderId, currentMessage.uid);
                        showMessage(currentAccountId, currentMessage.uid, currentMessage.folderId, specialUse, true).catch(() => { /* */ });
                    }
                } catch (e: any) {
                    const status = document.getElementById("status-sync");
                    if (status) status.textContent = `Watch failed: ${e?.message || e}`;
                }
            };
            banner.querySelector("#btn-flag-sender")?.addEventListener("click", () => onFlagToggle("sender", senderAddr));
            banner.querySelector("#btn-flag-domain")?.addEventListener("click", () => onFlagToggle("domain", senderDomain));

            // "Edit allowlist…" — fires a document-level event that app.ts
            // listens for and opens the JSONC editor pre-selected to
            // allowlist.jsonc. Keeps message-viewer free of the editor import.
            banner.querySelector("#btn-edit-allowlist")?.addEventListener("click", () => {
                document.dispatchEvent(new CustomEvent("mailx-open-jsonc-editor", { detail: { file: "allowlist.jsonc" } }));
            });

        }

        // bodyError / bodyErrorTransient retired in the local-first refactor.
        // Background body fetches that fail emit `bodyFetchError`, surfaced
        // by the module-level listener as a banner — no inline retry.

        // Body in sandboxed iframe
        if (msg.bodyHtml) {
            const iframe = document.createElement("iframe");
            iframe.sandbox.add("allow-same-origin");
            iframe.sandbox.add("allow-popups");
            iframe.sandbox.add("allow-popups-to-escape-sandbox");
            iframe.sandbox.add("allow-top-navigation-by-user-activation");
            // allow-scripts lets OUR injected <script> run (for Android link
            // interception — parent-side contentDocument listeners don't fire
            // reliably on Android WebView). CSP with a nonce restricts script
            // execution to our tag only; inline scripts in the email body are
            // still blocked.
            iframe.sandbox.add("allow-scripts");
            iframe.srcdoc = wrapHtmlBody(msg.bodyHtml, msg.remoteAllowed);
            // Two timing marks: text-painted (DOMContentLoaded, which fires
            // as soon as the HTML is parsed — typically tens of ms) and
            // all-loaded (load, which waits for every <img> to resolve and
            // can be 10+ s on a marketing email with remote content). The
            // user can read the email at the DOMContentLoaded mark; the
            // earlier single `load` mark misled the timing story.
            iframe.addEventListener("load", () => _ptick("iframe load (all resources)"), { once: true });
            const onReady = () => {
                const doc = iframe.contentDocument;
                if (!doc) return;
                const fire = () => _ptick("iframe DOMContentLoaded (text painted)");
                if (doc.readyState === "interactive" || doc.readyState === "complete") fire();
                else doc.addEventListener("DOMContentLoaded", fire, { once: true });
            };
            // srcdoc-driven iframes go through a single `load` lifecycle, but
            // the contentDocument is observable as soon as the iframe is in
            // the DOM. Schedule the readiness check after append.
            bodyEl.appendChild(iframe);
            queueMicrotask(onReady);
            _ptick("iframe srcdoc set");
            installPreviewControls(iframe);
        } else if (msg.bodyText) {
            // Plain text renders through the SAME sandboxed iframe as an HTML
            // body, as a <pre> of linkified text.
            //
            // It used to be a <pre> in the PARENT document, and that is why
            // "hovering over a URL or right mousing isn't working" in a
            // plain-text message (Bob 2026-08-09): every link affordance the
            // preview has — hover target, the right-click link menu (Open /
            // Copy link address / search), tap-to-open-externally — is
            // implemented for the iframe, by its injected script and its
            // contextmenu → previewContextMenu bridge. A detected URL isn't
            // "less of a URL" than an <a> the sender wrote, so it goes through
            // the same path and behaves identically, instead of the parent
            // document's generic fallback menu.
            //
            // Same reason as every other unification today: two renderers for
            // one job means every fix lands in one of them.
            // Oversized body: the service sent only the head, because the IPC
            // channel marshals replies through evaluate_script and a multi-MB
            // body takes the WebView2 renderer down with it. Say so — showing
            // a fraction of a letter with no notice is how a 9 MB bounce came
            // out looking like one sentence ending mid-word (Bob 2026-08-08).
            const trunc = (msg as any).bodyTruncated;
            if (trunc) {
                const mb = (n: number) => `${(n / 1_048_576).toFixed(1)} MB`;
                const note = document.createElement("div");
                note.className = "mv-system-message mv-system-error";
                note.style.cssText = "margin: 0 0 12px 0;";
                note.innerHTML = `<div class="mv-system-tag">mailx</div>
                    <div class="mv-system-title">Showing the first ${mb(trunc.sentBytes)} of a ${mb(trunc.totalBytes)} message</div>
                    <div class="mv-system-body">This message has no MIME structure, so its entire payload is one plain-text part; the remainder is almost always the base64 of a returned attachment.
                    The complete message is on disk:<br>
                    <code style="user-select:all;font-size:0.9em">${escapeHtml(trunc.emlPath || "(path unavailable)")}</code></div>`;
                bodyEl.appendChild(note);
            }

            const iframe = document.createElement("iframe");
            iframe.sandbox.add("allow-same-origin");
            iframe.sandbox.add("allow-popups");
            iframe.sandbox.add("allow-popups-to-escape-sandbox");
            iframe.sandbox.add("allow-top-navigation-by-user-activation");
            iframe.sandbox.add("allow-scripts");
            // `rmf-plain` is styled by wrapHtmlBody so the typography matches
            // what the parent <pre> used to produce.
            const head = msg.bodyText.length > PROGRESSIVE_TEXT_THRESHOLD
                ? msg.bodyText.slice(0, firstChunkEnd(msg.bodyText))
                : msg.bodyText;
            iframe.srcdoc = wrapHtmlBody(`<pre class="rmf-plain" id="rmf-plain">${linkifyText(head)}</pre>`, false);
            iframe.addEventListener("load", () => _ptick("iframe load (all resources)"), { once: true });
            bodyEl.appendChild(iframe);
            _ptick("iframe srcdoc set (plain text)");
            installPreviewControls(iframe);
            // Big body: stream the remainder into the iframe across animation
            // frames rather than parsing megabytes in one blocking gulp
            // (v1.2.229's reason, preserved — the work just happens inside the
            // iframe document now).
            if (msg.bodyText.length > head.length) {
                appendTextProgressively(iframe, msg.bodyText.slice(head.length), gen);
            }
        } else {
            // No bodyHtml AND no bodyText. The daemon thinks the body is
            // on disk (`cached === true`) but extracted nothing. Most
            // common: the body fetch errored earlier (network blip,
            // 401, 5xx), the row was marked cached anyway, and we have
            // an empty .eml. Surface the last known fetch error for
            // this uid if we have one — Bob 2026-05-09: "if there is a
            // fail to fetch then report the problem rather than telling
            // me there is no content."
            const fetchErr = recentFetchErrors.get(`${accountId}:${uid}`);
            if (fetchErr) {
                bodyEl.innerHTML = `<div class="mv-system-message mv-system-error">
                    <div class="mv-system-tag">mailx</div>
                    <div class="mv-system-title">Body fetch failed</div>
                    <div class="mv-system-body">${escapeHtml(fetchErr.error)}<br><span style="color:var(--color-text-muted);font-size:0.9em">Recorded ${Math.round((Date.now() - fetchErr.when) / 1000)}s ago. ${fetchErr.transient ? "Will retry automatically." : "Permanent — server-side delete may have raced."}</span></div>
                </div>`;
            } else {
                // Empty parse result. The COMMON case is benign — a
                // subject-only note (Bob 2026-05-22: "Remember Fish on
                // Monday" sent with no body), or an attachment-only message.
                // Lead with a calm one-liner, not the alarming parser dump.
                // The traceability crumbs (version, path, uid) stay, but
                // muted and compact — enough to debug a genuine stub without
                // making a normal empty message look broken.
                const emlPath = (msg as any).emlPath || "";
                const attCount = msg.attachments?.length || 0;
                const appVer = (document.querySelector(".app-version, #app-version, #status-version")?.textContent || "").trim();
                const headline = attCount > 0
                    ? "This message has attachments but no body text."
                    : "This message has no body text — only a subject.";
                const crumbs = `mailx${appVer ? " " + appVer : ""} · ${accountId}/${uid}${emlPath ? " · " + emlPath : ""}`;
                bodyEl.innerHTML = `<div class="mv-system-message">
                    <div class="mv-system-body">${escapeHtml(headline)}</div>
                    <div class="mv-system-body" style="color:var(--color-text-muted);font-size:0.8em;margin-top:8px">${escapeHtml(crumbs)}</div>
                </div>`;
            }
        }

        // Body is on screen and the renderer is still alive — clear the
        // crash marker so this message isn't treated as lethal next boot.
        markRenderDone();

        // Attachments — always clear first to avoid stale chips from previous message
        attEl.innerHTML = "";
        attEl.hidden = true;
        if (msg.attachments?.length) {
            attEl.hidden = false;
            // ── Drag-out prefetch cache ──
            // dataTransfer.setData ONLY works synchronously inside dragstart —
            // the drag data store closes at the first `await` — so the old
            // handler that fetched the attachment inside dragstart silently
            // set nothing, and drag-to-Explorer never worked (Bob 2026-07-02).
            // Prefetch every attachment's bytes in the background right after
            // the chips render; dragstart reads this cache synchronously.
            // Blob URLs from the previous message are revoked first.
            for (const u of dragOutUrls) { try { URL.revokeObjectURL(u); } catch { /* */ } }
            dragOutUrls.length = 0;
            const dragCache = new Map<number, { url: string; contentType: string }>();
            void (async () => {
                for (const a1 of msg.attachments) {
                    if ((a1.size || 0) > 50 * 1024 * 1024) continue; // don't buffer >50MB for a maybe-drag
                    try {
                        const data = await getAttachment(accountId, uid, a1.id, msg.folderId);
                        const bytes = Uint8Array.from(atob(data.content), c => c.charCodeAt(0));
                        const blob = new Blob([bytes], { type: data.contentType || "application/octet-stream" });
                        const url = URL.createObjectURL(blob);
                        dragOutUrls.push(url);
                        dragCache.set(a1.id, { url, contentType: data.contentType || "application/octet-stream" });
                    } catch { /* drag-out just won't arm for this attachment */ }
                }
            })();
            // Save (download to disk) one attachment by index — distinct from
            // the click handler's "open with the OS app". Desktop (WebView2 =
            // Chromium) gets a real Save-As dialog via showSaveFilePicker;
            // Android / --server fall back to blob + <a download> → Downloads
            // (the only save mechanism that works in every host).
            const saveAttachmentAt = async (idx: number): Promise<void> => {
                const a0 = msg.attachments[idx];
                const name = a0.filename || "attachment";
                try {
                    const data = await getAttachment(accountId, uid, a0.id, msg.folderId);
                    const bytes = Uint8Array.from(atob(data.content), c => c.charCodeAt(0));
                    const blob = new Blob([bytes], { type: data.contentType || "application/octet-stream" });
                    const picker = (window as any).showSaveFilePicker;
                    if (typeof picker === "function") {
                        // Native Save-As. A user cancel rejects with AbortError —
                        // that's not an error, so swallow it silently.
                        let handle: any;
                        try {
                            handle = await picker({ suggestedName: name });
                        } catch (e: any) {
                            if (e?.name === "AbortError") return;
                            throw e;
                        }
                        const writable = await handle.createWritable();
                        await writable.write(blob);
                        await writable.close();
                        return;
                    }
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = name; a.style.display = "none";
                    document.body.appendChild(a); a.click();
                    setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
                } catch (err: any) {
                    window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: `Couldn't save "${name}": ${err?.message || err}`, key: "attachment-save" } }));
                }
            };
            const saveAllAttachments = async (): Promise<void> => {
                // Stagger so the host registers each download as a separate file
                // instead of collapsing rapid-fire blob clicks.
                for (let k = 0; k < msg.attachments.length; k++) {
                    await saveAttachmentAt(k);
                    await new Promise(r => setTimeout(r, 300));
                }
            };
            for (let i = 0; i < msg.attachments.length; i++) {
                const att = msg.attachments[i];
                // Button (not <a href="#">): the browser/WebView2 native
                // context menu offered "Open in new window" which navigated
                // to msger.localhost/index.html# and 404'd. Switching to a
                // button removes that path; download happens via a
                // programmatic <a download> click which is reliable across
                // msger / WebView2 / Android WebView (window.open of a blob
                // URL is silently blocked in some hosts).
                const chip = document.createElement("button");
                chip.type = "button";
                chip.className = "mv-att-chip";
                chip.textContent = `\uD83D\uDCCE ${att.filename} (${formatSize(att.size)})`;
                chip.title = `${att.filename} (${att.mimeType})`;
                chip.addEventListener("click", async (e) => {
                    e.preventDefault();
                    // Click feedback + re-click guard. Opening hands off to the
                    // OS viewer (~200ms) with no visible change in mailx, so the
                    // user used to click 3-4 times. Show "Opening…" for at least
                    // 600ms and ignore clicks while it's in flight.
                    if (chip.dataset.opening === "1") return;
                    chip.dataset.opening = "1";
                    const chipLabel = chip.textContent || "";
                    chip.textContent = "⏳ Opening…";
                    const tStart = Date.now();
                    const restoreChip = (): void => {
                        const wait = Math.max(0, 600 - (Date.now() - tStart));
                        setTimeout(() => { chip.textContent = chipLabel; chip.dataset.opening = ""; }, wait);
                    };
                    try {
                        const bridge = (window as any)._nativeBridge;
                        if (bridge?.openAttachment) {
                            // Android: blob URLs don't work in WebView. Pass base64
                            // to native bridge which saves to Downloads and opens
                            // with the system viewer.
                            const data = await getAttachment(accountId, uid, att.id, msg.folderId);
                            await bridge.openAttachment(att.filename, data.contentType, data.content);
                            return;
                        }
                        // Desktop (IPC): the Node service saves the file and opens
                        // it with the OS default app. openAttachment() returns the
                        // result object when an IPC host handled it, or `undefined`
                        // only when the host has no such method (real browser /
                        // --server mode). A genuine service failure throws \u2014 we do
                        // NOT blob-fall-back in that case (the service is the only
                        // thing that can open a file on the desktop, and the blob
                        // path just produced a misleading second error).
                        const res = await openAttachment(accountId, uid, att.id, msg.folderId, att.filename);
                        if (res) return;
                        // res === undefined \u2192 real browser / --server mode only.
                        const data = await getAttachment(accountId, uid, att.id, msg.folderId);
                        const bytes = Uint8Array.from(atob(data.content), c => c.charCodeAt(0));
                        const blob = new Blob([bytes], { type: data.contentType });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = att.filename || "attachment";
                        a.style.display = "none";
                        document.body.appendChild(a);
                        a.click();
                        // Defer revoke until the download has had time to start.
                        setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
                    } catch (err: any) {
                        // Non-blocking banner \u2014 never alert(): a modal alert
                        // freezes the WebView event loop, which stalls IPC event
                        // delivery and makes sync look like it stopped.
                        const m = `Couldn't open "${att.filename}": ${err?.message || err}`;
                        console.error(m);
                        window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: m, key: "attachment-open" } }));
                    } finally {
                        restoreChip();
                    }
                });
                // Drag the chip to an external target (Explorer / Finder) to
                // drop the file there. Chromium `DownloadURL` dataTransfer
                // format: "mime:filename:blob-url". MUST be synchronous — the
                // content comes from the prefetch cache above; Chromium stamps
                // the dropped file with the Mark-of-the-Web like any download.
                chip.draggable = true;
                chip.addEventListener("dragstart", (e) => {
                    if (!e.dataTransfer) return;
                    const cached = dragCache.get(att.id);
                    if (!cached) {
                        // Prefetch still in flight (or >50MB skip). Cancel the
                        // drag rather than start one that can't carry data.
                        e.preventDefault();
                        const statusEl = document.getElementById("status-sync");
                        if (statusEl) statusEl.textContent = "Attachment still loading — try dragging again in a moment.";
                        return;
                    }
                    // Sanitize filename: no path separators, no newlines.
                    const safeName = (att.filename || "attachment").replace(/[\r\n"\/\\]/g, "_");
                    e.dataTransfer.setData("DownloadURL", `${cached.contentType}:${safeName}:${cached.url}`);
                    e.dataTransfer.effectAllowed = "copy";
                });
                // Right-click → Open / Save / Save all. Click still opens; this
                // adds explicit save (download to disk) which the click path
                // didn't offer (Bob 2026-06-04).
                chip.addEventListener("contextmenu", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const items: { label: string; action: () => void; separator?: boolean }[] = [
                        { label: "Open", action: () => chip.click() },
                        { label: `Save "${att.filename}"…`, action: () => { void saveAttachmentAt(i); } },
                    ];
                    if (msg.attachments.length > 1) {
                        items.push({ label: `Save all (${msg.attachments.length})…`, action: () => { void saveAllAttachments(); } });
                    }
                    // Delete = real message surgery (local strip + server
                    // replace via the sync queue) — confirmed, not instant.
                    // Desktop-only today; Android's bridge lacks the method.
                    if ((window as any).mailxapi?.platform !== "android") {
                        items.push({ label: `Delete "${att.filename}"…`, separator: true, action: () => {
                            confirmDeleteAttachments(msg, accountId, uid, [att.id], `Delete "${att.filename}" from this message?`);
                        } });
                        if (msg.attachments.length > 1) {
                            items.push({ label: `Delete all (${msg.attachments.length})…`, action: () => {
                                confirmDeleteAttachments(msg, accountId, uid, "all", `Delete all ${msg.attachments.length} attachments from this message?`);
                            } });
                        }
                    }
                    showContextMenu(e.clientX, e.clientY, items, { editTarget: e.target });
                });
                attEl.appendChild(chip);
            }
        }
    } catch (e: any) {
        const err = e.message || "Unknown error";
        console.error("showMessage error:", e);
        // A body-fetch failure (corrupt/unrelated .eml, parse throw) must NOT
        // leave the header hidden. Line 528 hides it on the no-cache path; if
        // we then throw, the user got an error body with no From/Subject/Date
        // and no Reply/Source/Delete toolbar — "have a post but can't see the
        // header portion" (Bob 2026-06-05). Render the header from whatever
        // envelope we have so the user always sees WHICH message failed and
        // can still act on it. renderHeaderFromEnvelope unhides + is defensive.
        const failEnv = cached || lastEnvelope || envelope;
        if (failEnv) { try { renderHeaderFromEnvelope(headerEl, failEnv); } catch { /* */ } }
        // "Message was deleted from the server" — the service already dropped
        // the local row. Remove it from the list so the UI advances to the next
        // message instead of sitting on a stale error banner.
        const isNotFound = /deleted from the server|isNotFound|not found|Not Found|404/.test(err);
        if (isNotFound) {
            // Drop the stale row so the list auto-advances to the next message
            // (or clears the viewer). Leaves the user a way back on mobile where
            // the viewer takes the whole screen. The list owns focus handoff —
            // we surface the removal as an event and let it run its own
            // removeMessages flow (which both filters the list and re-focuses
            // a survivor or clears this pane).
            document.dispatchEvent(new CustomEvent("mailx-remove-stale", {
                detail: { accountId, uid },
            }));
            return;
        }
        if (retryCount < 3) {
            retryCount++;
            bodyEl.innerHTML = `<div class="mv-empty">Loading failed: ${err} — retrying (${retryCount}/3)...</div>`;
            setTimeout(() => { if (gen === showMessageGeneration) showMessage(accountId, uid, folderId, specialUse, true); }, 3000);
        } else {
            bodyEl.innerHTML = `<div class="mv-empty">Failed to load: ${err}</div>`;
        }
    }
}

function formatAddr(addr: { name: string; address: string }): string {
    // formatSender collapses the redundant `"x@y.com" <x@y.com>` form (Microsoft
    // 365 quarantine notices and plenty of bulk senders emit it; showing the
    // same address twice reads as if two parties were involved — Bob
    // 2026-08-19) and reports when a display name asserts a DIFFERENT address
    // than the envelope's, which is the display-name spoof.
    const { text, claimed } = formatSender(addr);
    // Approved senders are never called out — see approved-senders.ts.
    if (!claimed || isSenderApproved(addr.address)) return text;
    // Never let the claim stand unqualified: name the real address explicitly.
    return `${text} — display name claims ${claimed}`;
}

/** Above this many To+Cc addresses the recipient line is offered collapsed
 *  with a "▸ N recipients" expander. Anything under it reads fine inline. */
const MANY_RECIPIENTS = 6;

/** Ceiling on how many addresses the header's right-click menu enumerates.
 *  Each address contributes ~7 entries (copy name / copy address / contacts /
 *  preferred / denylist / priority), so a 444-recipient bulk mail would build
 *  a 3000-item menu taller than the screen and slow to open. */
const CONTEXT_MENU_ADDR_CAP = 12;

/**
 * Fill the viewer's To/Cc line.
 *
 * Written as elements rather than a bare `textContent` because bulk mail can
 * carry hundreds of recipients: the full text stays in the DOM (so find,
 * select and copy still see every address) but `.mv-to-text` is line-clamped
 * to two rows, with a toggle to expand. Unclamped, that one line grew the
 * header past the whole viewer height and starved `.mv-body` to zero — see
 * the `.mv-header` comment in components.css.
 */
function setRecipientLine(
    toEl: Element,
    to: { name: string; address: string }[] | undefined,
    cc: { name: string; address: string }[] | undefined,
    deliveredTo?: string,
): void {
    const toList = to || [];
    const ccList = cc || [];
    let line = `To: ${toList.map(formatAddr).join(", ")}`;
    if (ccList.length) line += `  Cc: ${ccList.map(formatAddr).join(", ")}`;
    // Always-visible Delivered-To — shown when present and not already covered
    // by the To/Cc list. Critical for accounts with multiple aliases where you
    // need to see which one received the message at a glance.
    if (deliveredTo) {
        const dt = deliveredTo.toLowerCase();
        const covered = [...toList, ...ccList].some(a => a.address.toLowerCase() === dt);
        if (!covered) line += `  Delivered-To: ${deliveredTo}`;
    }

    toEl.textContent = "";
    toEl.classList.remove("mv-to-expanded");
    const text = document.createElement("span");
    text.className = "mv-to-text";
    text.textContent = line;
    toEl.append(text);

    const count = toList.length + ccList.length;
    // Offer the expander when the list is long by count, or when the clamp
    // actually cut something off (a handful of very long display names).
    const clamped = text.scrollHeight > text.clientHeight + 1;
    if (count <= MANY_RECIPIENTS && !clamped) return;

    const toggle = document.createElement("button");
    toggle.className = "mv-to-toggle";
    toggle.type = "button";
    const label = count === 1 ? "1 recipient" : `${count} recipients`;
    const paint = (open: boolean) => {
        toggle.textContent = `${open ? "▾" : "▸"} ${label}`;
        toggle.title = open ? "Collapse the recipient list" : "Show the full recipient list";
    };
    paint(false);
    toggle.addEventListener("click", () => paint(toEl.classList.toggle("mv-to-expanded")));
    toEl.append(toggle);
}

/** Render the viewer header from a list-row envelope (instant — no body
 *  fetch awaited). Used to populate the header pane the moment a message
 *  is clicked so the user always sees something actionable; getMessage()
 *  later overwrites the same fields with the authoritative values from the
 *  body parse (which can add Cc, Delivered-To, etc. that the list row
 *  doesn't track). */
function renderHeaderFromEnvelope(headerEl: HTMLElement, env: any): void {
    headerEl.hidden = false;
    const fromEl = headerEl.querySelector(".mv-from");
    const toEl = headerEl.querySelector(".mv-to");
    const subjEl = headerEl.querySelector(".mv-subject");
    const dateEl = headerEl.querySelector(".mv-date");
    if (fromEl) fromEl.textContent = formatAddr(env.from);
    if (toEl) setRecipientLine(toEl, env.to, env.cc);
    if (subjEl) subjEl.textContent = env.subject || "";
    if (dateEl) {
        try { dateEl.textContent = new Date(env.date).toLocaleString(); }
        catch { dateEl.textContent = ""; }
    }
}

function escapeHtml(s: string): string {
    return (s || "").replace(/[&<>"']/g, c =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]!));
}

/** Convert plain text URLs into clickable links, escaping HTML */
/** Plain-text bodies above this go through the chunked renderer. Below it, a
 *  single innerHTML is faster than the scheduling overhead. */
const PROGRESSIVE_TEXT_THRESHOLD = 200 * 1024;
/** Work per animation frame. Small enough that a frame stays well under the
 *  16 ms budget on a slow machine; large enough that a 1.4 MB body finishes in
 *  a couple of dozen frames rather than hundreds. */
const TEXT_CHUNK_BYTES = 64 * 1024;

/** Where the first chunk of a large plain-text body ends — cut on a newline
 *  so a URL is never split across chunks (half a URL linkifies wrong). */
function firstChunkEnd(text: string): number {
    const end = Math.min(TEXT_CHUNK_BYTES, text.length);
    if (end >= text.length) return text.length;
    const nl = text.lastIndexOf("\n", end);
    return nl > 0 ? nl + 1 : end;
}

/**
 * Stream the rest of a large plain-text body into the preview iframe, one
 * line-aligned chunk per animation frame, linkifying each chunk as it goes.
 *
 * Why chunked at all: linkify + HTML parse + layout over a multi-megabyte
 * body is synchronous, so doing it in one statement freezes the window long
 * enough for WebView2 to report the renderer unresponsive (Bob 2026-08-08,
 * "oh — just took forever"). A Web Worker cannot help: workers have no DOM,
 * and the DOM work IS the expensive half. Coming up for air between chunks is
 * what keeps the app live, and the text visibly streams in.
 *
 * Aborts if the user moves to another message mid-render (`gen` against
 * showMessageGeneration, the same staleness guard the IPC path uses) or if
 * the iframe is torn out from under us.
 */
function appendTextProgressively(iframe: HTMLIFrameElement, rest: string, gen: number): void {
    const chunks: string[] = [];
    let at = 0;
    while (at < rest.length) {
        let end = Math.min(at + TEXT_CHUNK_BYTES, rest.length);
        if (end < rest.length) {
            const nl = rest.lastIndexOf("\n", end);
            if (nl > at) end = nl + 1;
        }
        chunks.push(rest.slice(at, end));
        at = end;
    }
    let i = 0;
    const step = (): void => {
        if (gen !== showMessageGeneration) return;          // another message is on screen
        const doc = iframe.contentDocument;
        const host = doc?.getElementById("rmf-plain");
        if (!doc || !host) return;                          // iframe replaced or not ready
        if (i >= chunks.length) return;
        const span = doc.createElement("span");
        span.innerHTML = linkifyText(chunks[i++]);
        host.appendChild(span);
        // Matches in a chunk that arrives after the first paint would stay
        // unmarked — re-collect once the tail is in.
        if (i >= chunks.length) refreshSearchHighlight(iframe);
        requestAnimationFrame(step);
    };
    // The iframe document may not exist for a frame or two after srcdoc is
    // set; poll briefly rather than dropping the tail on the floor.
    let waits = 0;
    const begin = (): void => {
        if (gen !== showMessageGeneration) return;
        if (iframe.contentDocument?.getElementById("rmf-plain")) { step(); return; }
        if (waits++ > 120) return;                          // ~2 s at 60 fps — give up quietly
        requestAnimationFrame(begin);
    };
    requestAnimationFrame(begin);
}

function linkifyText(text: string): string {
    // Escape HTML first
    const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Then linkify URLs. The character class already stops the match at
    // whitespace and <>"')] but NOT at sentence punctuation, so a URL that
    // ends a clause — "…25965.pdf, in case…" — would otherwise pull the
    // trailing comma into the link (Bob 2026-06-11). Peel a trailing run of
    // sentence punctuation off the match and emit it as plain text after the
    // anchor. The run must be pure punctuation, so an internal dot like the
    // ".pdf" extension is untouched (only the final ",", ".", etc. is shed).
    return escaped.replace(
        /(https?:\/\/[^\s<>"')\]]+)/g,
        (match: string) => {
            const m = match.match(/^(.*?)([.,;:!?]+)$/);
            const url = m ? m[1] : match;
            const trailer = m ? m[2] : "";
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${trailer}`;
        }
    );
}

function escapeText(s: string): string {
    const div = document.createElement("div");
    div.textContent = s;
    return div.innerHTML;
}

/** Minimal add-contact modal: name + email + organization with a duplicate
 *  check (checks the contacts DB for an existing row with the same email
 *  and surfaces it so the user can update instead of creating a second
 *  row with a different name). Future: AI-extracted fields from the letter
 *  body populate the form before it opens. */
async function showAddContactDialog(nameIn: string, emailIn: string): Promise<void> {
    let dup: { name: string; email: string; source: string } | null = null;
    try {
        const existing = await listContacts(emailIn, 1, 10);
        const match = (existing?.items || []).find((c: any) => (c.email || "").toLowerCase() === emailIn.toLowerCase());
        if (match) dup = match;
    } catch { /* non-fatal — dialog still works without dup info */ }

    // Source-aware status line. "discovered" = local cache auto-collected from
    // mail, NOT in Google Contacts — the old "Already in address book" wording
    // conflated the two and confused Bob (2026-06-04). Make the distinction
    // explicit, and say what Save actually does (upsertContact pushes to Google).
    let dupInfo = "";
    if (dup) {
        const nm = `<strong>${escapeText(dup.name || "(no name)")}</strong>`;
        if (dup.source === "discovered") {
            dupInfo = `<div class="mailx-modal-info">Found in <em>local discovered contacts</em> (auto-collected from your mail) as ${nm} — <strong>not yet in Google Contacts</strong>. Saving adds it to Google Contacts.</div>`;
        } else if (dup.source === "google") {
            dupInfo = `<div class="mailx-modal-info">Already in <strong>Google Contacts</strong> as ${nm}. Saving updates the name.</div>`;
        } else {
            dupInfo = `<div class="mailx-modal-info">Already saved as ${nm} (${escapeText(dup.source)}). Saving updates the name and syncs to Google Contacts.</div>`;
        }
    }
    // Button label: a discovered (local-only) contact is being PROMOTED to
    // Google, so "Add to Google Contacts" is truthful; an existing Google
    // contact is an "Update".
    const saveLabel = dup ? (dup.source === "discovered" ? "Add to Google Contacts" : "Update") : "Save";
    const backdrop = document.createElement("div");
    backdrop.className = "mailx-modal-backdrop";
    const panel = document.createElement("div");
    panel.className = "mailx-modal";
    panel.innerHTML = `
        <div class="mailx-modal-title">
            <span class="mailx-modal-title-text">${dup ? "Update contact" : "Add contact"}</span>
            <button type="button" class="mailx-modal-close" id="ac-close" aria-label="Close">&times;</button>
        </div>
        ${dupInfo}
        <label class="mailx-modal-label">Name
            <input class="mailx-modal-input" id="ac-name" type="text" value="${escapeText(dup?.name || nameIn || "")}" autofocus>
        </label>
        <label class="mailx-modal-label">Email
            <input class="mailx-modal-input" id="ac-email" type="email" value="${escapeText(emailIn)}" readonly>
        </label>
        <label class="mailx-modal-label">Organization <span style="color:var(--color-text-muted);font-size:0.85em">(optional)</span>
            <input class="mailx-modal-input" id="ac-org" type="text" placeholder="">
        </label>
        <div class="mailx-modal-buttons">
            <span class="mailx-modal-spacer"></span>
            <button type="button" class="mailx-modal-btn" data-action="cancel">Cancel</button>
            <button type="button" class="mailx-modal-btn mailx-modal-btn-primary" data-action="save">${escapeText(saveLabel)}</button>
        </div>`;
    backdrop.appendChild(panel);
    document.body.appendChild(backdrop);

    const close = () => backdrop.remove();
    panel.querySelector<HTMLButtonElement>("#ac-close")!.addEventListener("click", close);
    panel.querySelectorAll<HTMLButtonElement>(".mailx-modal-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
            if (btn.dataset.action === "cancel") { close(); return; }
            const nameEl = panel.querySelector<HTMLInputElement>("#ac-name")!;
            const emailEl = panel.querySelector<HTMLInputElement>("#ac-email")!;
            btn.disabled = true;
            btn.textContent = "Saving…";
            try {
                // upsertContact is the two-way cache path (enqueues a Google
                // People push); for pure local-first addContact would also
                // work but skips the Google sync. Use upsertContact so the
                // row propagates to Google Contacts next drain tick.
                await upsertContact(nameEl.value.trim(), emailEl.value.trim());
                close();
            } catch (e: any) {
                btn.disabled = false;
                btn.textContent = saveLabel;
                alert(`Couldn't save: ${e?.message || e}`);
            }
        });
    });
    const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") { close(); document.removeEventListener("keydown", onKey, true); }
    };
    document.addEventListener("keydown", onKey, true);
    // addContact is kept as a legacy silent path (no-form) for any caller
    // that still invokes it — currently none after this refactor.
    void addContact;
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function wrapHtmlBody(html: string, allowRemote = false): string {
    // CSP blocks remote resources (tracking pixels, external CSS). Inline
    // scripts are allowed via 'unsafe-inline' so our injected link-tap handler
    // runs; email-body <script> tags and on* handlers are stripped server-side
    // by sanitizeHtml() in mailx-types, so this doesn't actually widen the
    // attack surface. (A per-render nonce would be tidier, but meta-CSP with
    // nonces isn't reliably honored across older WebViews — and when a nonce
    // is present, 'unsafe-inline' is ignored, so our script fell back to
    // blocked on those WebViews.)
    const csp = allowRemote
        ? ""
        : `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: cid:; form-action 'none';">`;
    return `<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
${csp}
<style>
    html, body { touch-action: pan-y pinch-zoom; }
    html { height: 100%; overflow-y: auto; overflow-x: hidden; }
    body {
        font-family: system-ui, sans-serif;
        /* 17.5px x the 1.15 default preview zoom = ~20.1px effective, which is
           what COMPOSE_FONT_DEFAULT_PX (compose.ts) is set to match. Change one
           and change the other, or reading and writing drift apart again. */
        font-size: 17.5px;
        line-height: 1.5;
        color: #1a1a2e;
        background: #fff;
        padding: 1rem;
        margin: 0;
        min-height: 100%;
        word-break: break-word;
        color-scheme: dark light;
    }
    /* max-width keeps wide images inside the pane; modern WebView2 preserves
       aspect ratio from the width/height attributes automatically when it
       shrinks them, so we must NOT force height:auto. Forcing it overrode the
       explicit pixel height on sliced layout images (e.g. a 12x226 rounded-
       corner slice) — when an email's responsive @media rule widened the
       slice's cell, height:auto recomputed height from the tall aspect ratio
       and ballooned the slice to full-page height, pushing all text far below
       the fold (Bob 2026-06-10: Chase fraud-alert "scroll way down to see text"). */
    img { max-width: 100%; }
    a { color: #1a6dd4; }
    pre, code { white-space: pre-wrap; }
    /* Plain-text bodies render as this <pre>. Sans-serif at the same size and
       line-height as an HTML body, so two messages in one thread don't look
       like they came from different apps (Bob 2026-05-12: "why the font
       change"). */
    .rmf-plain {
        margin: 0;
        padding: 0;
        font-family: system-ui, sans-serif;
        font-size: 17.5px;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
    }
    blockquote { border-left: 3px solid #ccc; padding-left: 1rem; margin-left: 0; color: #666; }
    /* Search matches. Painted by the parent through the CSS Custom Highlight
       API (CSS.highlights) — no <mark> in the message DOM, so the letter the
       user copies, quotes or re-renders is byte-identical to what arrived.
       Both colors are explicit: a highlight that inherits the body color is
       invisible against its own background in one theme or the other. */
    ::highlight(mailx-find) { background: #ffd54a; color: #1a1a2e; }
    @media (prefers-color-scheme: dark) {
        body { color: #cdd6f4; background: #282840; }
        a { color: #89b4fa; }
        blockquote { border-color: #45475a; color: #6c7086; }
    }
</style>
<base target="_blank">
<script>
// Link interception — Android WebView doesn't fire the default <a target="_blank">
// new-window handler, so we postMessage to the parent which routes through the
// native bridge (mailxapi://openurl) to Launcher.OpenAsync.
(function () {
    function handleLinkTap(e) {
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        if (!a) return;
        var url = a.href;
        if (!url || url.indexOf("javascript:") === 0 || url.charAt(0) === "#") return;
        e.preventDefault();
        e.stopPropagation();
        try { console.log("[android-link] preview tap intercepted → " + url); } catch (e2) { /* */ }
        window.parent.postMessage({ type: "linkClick", url: url }, "*");
    }
    document.addEventListener("click", handleLinkTap, true);
    // Remote-image failure diagnostic. Bob 2026-07-28: an allowlisted
    // Nextdoor digest rendered every https image as a broken icon while the
    // same URLs fetched fine from curl — untraceable after the fact because
    // image load failures are silent. Report failures to the parent, which
    // logs them to the daemon log with the first failing URL.
    window.addEventListener("load", function () {
        setTimeout(function () {
            try {
                var bad = [];
                var imgs = document.images || [];
                for (var i = 0; i < imgs.length; i++) {
                    var im = imgs[i];
                    if (im.complete && im.naturalWidth === 0 && /^https?:/.test(im.src)) bad.push(im.src);
                }
                if (bad.length) {
                    window.parent.postMessage({ type: "imgLoadFail", count: bad.length, total: imgs.length, first: String(bad[0]).slice(0, 160) }, "*");
                }
            } catch (e3) { /* diagnostic only */ }
        }, 3000);
    });
    // Android WebView fallback: some builds drop the synthetic click after
    // touchend, so treat a stationary touchstart→touchend on the same link
    // as a tap. Anything that moves more than TAP_SLOP pixels is a scroll
    // and must NOT activate the link.
    var TAP_SLOP = 10;
    var lastTouchTarget = null;
    var lastTouchX = 0;
    var lastTouchY = 0;
    var touchMoved = false;
    // All touch listeners are passive so Android WebView can compositor-scroll
    // the iframe without waiting on our JS. handleLinkTap's preventDefault only
    // matters for the "click" path (which is non-passive by default).
    document.addEventListener("touchstart", function (e) {
        var t0 = e.touches && e.touches[0];
        lastTouchX = t0 ? t0.clientX : 0;
        lastTouchY = t0 ? t0.clientY : 0;
        touchMoved = false;
        lastTouchTarget = (e.target && e.target.closest) ? e.target.closest("a[href]") || e.target : e.target;
    }, { passive: true, capture: true });
    document.addEventListener("touchmove", function (e) {
        if (touchMoved) return;
        var t = e.touches && e.touches[0];
        if (!t) return;
        if (Math.abs(t.clientX - lastTouchX) > TAP_SLOP || Math.abs(t.clientY - lastTouchY) > TAP_SLOP) {
            touchMoved = true;
            lastTouchTarget = null;
        }
    }, { passive: true, capture: true });
    document.addEventListener("touchend", function (e) {
        if (touchMoved) { lastTouchTarget = null; touchMoved = false; return; }
        var t = (e.target && e.target.closest) ? e.target.closest("a[href]") || e.target : e.target;
        if (lastTouchTarget && lastTouchTarget === t) handleLinkTap(e);
        lastTouchTarget = null;
    }, { passive: true, capture: true });
    document.addEventListener("touchcancel", function () {
        lastTouchTarget = null; touchMoved = false;
    }, { passive: true, capture: true });
    // Link hover preview — posts the link URL to the parent on hover so
    // the parent can show a tooltip after a 500 ms dwell. Earlier removal
    // (2026-04-24) was because dismissers in the parent (mousedown / scroll
    // / blur) don't fire while the mouse is inside the iframe, leaving the
    // popover stuck. Restored 2026-05-09 with the proper fix: the iframe
    // explicitly posts an empty-URL message on link mouseout AND on body
    // mousemove-away, so the parent reliably hides regardless of which
    // dismissers it sees. The parent's debounced 500 ms show-timer still
    // suppresses flicker.
    var lastHoveredHref = "";
    // The <img> under the most recent right-click, kept so a later
    // "copyImage" command can encode that exact element (see below).
    var lastContextImg = null;
    function postLinkHover(href, rect, mouse) {
        // Only post on transitions to avoid spamming the parent on every
        // mousemove inside a link.
        if (href === lastHoveredHref) return;
        lastHoveredHref = href;
        window.parent.postMessage({
            type: "linkHover",
            url: href,
            rect: rect ? { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom } : null,
            // Cursor position (iframe-viewport coords) so the parent can anchor
            // the tooltip AT the pointer, not at the link's bottom-left corner —
            // on a wide link/banner the corner is far from the cursor (Bob
            // 2026-06-13: "displays the URL far from the cursor").
            mouse: mouse || null,
        }, "*");
    }
    document.addEventListener("mouseover", function (e) {
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        if (!a) return;
        var href = a.getAttribute("href") || "";
        if (!href || href.charAt(0) === "#" || href.indexOf("javascript:") === 0) return;
        var r = a.getBoundingClientRect();
        postLinkHover(a.href || href, r, { x: e.clientX, y: e.clientY });
    }, true);
    document.addEventListener("mouseout", function (e) {
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        if (!a) return;
        // Only fire when leaving the link entirely (not into a child element).
        var to = e.relatedTarget;
        if (to && to.closest && to.closest("a[href]") === a) return;
        postLinkHover("", null);
    }, true);
    // Mouseleave on body covers the case where the cursor exits the
    // iframe entirely without firing mouseout on the link (Edge / older
    // WebView2 builds). Belt-and-suspenders dismissal.
    document.addEventListener("mouseleave", function () {
        if (lastHoveredHref) postLinkHover("", null);
    }, true);
    // Touch dismissers — a tap on Android fires synthetic mouseover but
    // often skips the matching mouseout (no mouse-move to track), so the
    // URL preview can stick on top of the message body even after the
    // user has scrolled away (Bob 2026-05-26 "sticky overlay URL"). Any
    // touchstart / touchend / scroll anywhere in the iframe immediately
    // clears the tooltip.
    var clearHover = function () { if (lastHoveredHref) postLinkHover("", null); };
    document.addEventListener("touchstart", clearHover, { passive: true, capture: true });
    document.addEventListener("touchend",   clearHover, { passive: true, capture: true });
    document.addEventListener("scroll",     clearHover, { passive: true, capture: true });
    window.addEventListener("scroll",       clearHover, { passive: true, capture: true });
    // Note: iframe-level dblclick fullscreen toggle REMOVED — it hijacked
    // word-selection (Bob 2026-05-11). Double-clicking text inside the
    // preview now selects the word like any other browser surface; the
    // fullscreen toggle lives on the viewer chrome (mv-header) handler
    // in app.ts, which explicitly excludes interactive controls but
    // operates on chrome, not iframe content.

    // Receive commands from the parent (Copy / Select all menu actions).
    // Browsers restrict execCommand("copy") to the document where focus
    // lives — running it in the parent against the iframe's selection is
    // a no-op, so the parent posts the command in and we run it here.
    window.addEventListener("message", function (e) {
        var d = e.data;
        if (!d || d.type !== "previewCommand") return;
        if (d.cmd === "copy") {
            try { document.execCommand("copy"); } catch (_) {}
        } else if (d.cmd === "selectAll") {
            try {
                var s = window.getSelection && window.getSelection();
                if (s) {
                    var r = document.createRange();
                    r.selectNodeContents(document.body);
                    s.removeAllRanges();
                    s.addRange(r);
                }
            } catch (_) {}
        } else if (d.cmd === "copyImage") {
            // Encode the right-clicked image HERE and hand the parent the
            // bytes. The parent can't fetch it itself: an inline image is a
            // multi-megabyte data: URI it would have to re-download through
            // the message channel, and a remote one is cross-origin with no
            // CORS headers, so a parent-side fetch rejects. This img is
            // already decoded here, so a canvas draw needs no network at all.
            // (A remote image DOES taint the canvas — toBlob then throws and
            // the parent falls back to fetching it through the daemon.)
            var target = lastContextImg;
            if (!target && d.src) {
                var all = document.getElementsByTagName("img");
                for (var i = 0; i < all.length; i++) {
                    if (all[i].currentSrc === d.src || all[i].src === d.src) { target = all[i]; break; }
                }
            }
            if (!target) {
                window.parent.postMessage({ type: "previewImageBytes", reqId: d.reqId, error: "image element not found" }, "*");
                return;
            }
            try {
                var cv = document.createElement("canvas");
                cv.width = target.naturalWidth || target.width;
                cv.height = target.naturalHeight || target.height;
                if (!cv.width || !cv.height) throw new Error("image has no decoded size yet");
                cv.getContext("2d").drawImage(target, 0, 0);
                cv.toBlob(function (b) {
                    if (!b) {
                        window.parent.postMessage({ type: "previewImageBytes", reqId: d.reqId, error: "encode failed" }, "*");
                        return;
                    }
                    window.parent.postMessage({ type: "previewImageBytes", reqId: d.reqId, blob: b }, "*");
                }, "image/png");
            } catch (err) {
                window.parent.postMessage({
                    type: "previewImageBytes", reqId: d.reqId,
                    error: (err && err.message) ? err.message : String(err)
                }, "*");
            }
        }
    });

    // Right-click → unified context menu. Always send 'previewContextMenu'
    // with both link info (when over a link) AND selection / body access.
    // Quora-style pages are wall-to-wall links — branching on target meant
    // body actions (Translate / Zoom) were unreachable. Combined menu
    // shows link actions first when applicable, body actions always.
    // Suppresses the native WebView2 menu unconditionally — without that,
    // its Back/Refresh/Save-as/Inspect menu fires before the parent
    // contentDocument listener attached.
    document.addEventListener("contextmenu", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var rect = { left: 0, top: 0 };
        try { rect = e.target.getBoundingClientRect ? e.target.getBoundingClientRect() : rect; } catch (_) {}
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        var img = e.target && e.target.closest ? e.target.closest("img") : null;
        // Remember the element itself, not just its src — "Copy image" encodes
        // this exact <img>, and matching by src would pick the wrong one when
        // a letter repeats the same image (spacers, logos, tracking pixels).
        lastContextImg = img;
        var sel = (window.getSelection && window.getSelection()) ? window.getSelection().toString() : "";
        window.parent.postMessage({
            type: "previewContextMenu",
            selectedText: sel,
            linkUrl: a ? a.href : "",
            linkText: a ? (a.textContent || "").slice(0, 100) : "",
            imgSrc: img ? (img.currentSrc || img.src || "") : "",
            x: e.clientX, y: e.clientY,
            iframeLeft: rect.left, iframeTop: rect.top
        }, "*");
    });
    // Iframe pointerdown → tell the parent to dismiss any open context menu.
    // The parent's dismissListener listens to its own document's pointerdown,
    // but iframe events don't bubble across the boundary; without this, a
    // click in the message body to dismiss the menu would be invisible to
    // the parent and the menu would stick open.
    document.addEventListener("pointerdown", function (e) {
        // The contextmenu handler above ALSO fires pointerdown on right-click;
        // that one opens a fresh menu, which already closes the previous.
        // Skip right-click (button 2) to avoid a publish/close race.
        if (e.button === 2) return;
        try { window.parent.postMessage({ type: "iframePointerDown" }, "*"); } catch (_) {}
    }, true);
    // Key forwarding — Delete, Ctrl+D, arrow keys, etc. need to reach app.ts
    // even when focus is inside the sandboxed iframe. Parent-side
    // contentDocument listeners (see installPreviewControls) work on
    // desktop WebView2 but not Android WebView, so we post every keydown
    // that isn't plain typing.
    document.addEventListener("keydown", function (e) {
        var t = e.target;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
        // Zoom keys handled by parent-side installPreviewControls; don't double-send.
        if (e.ctrlKey && (e.key === "=" || e.key === "+" || e.key === "-" || e.key === "0")) return;
        // Ctrl+A stays in the preview: native select-all on THIS document so
        // the letter text can be copied. Don't suppress, don't forward — the
        // list's select-all-rows must not fire while focus is in the preview
        // (Bob 2026-07-18). Mirrors the parent-side handler's early return.
        var klc = (e.key || "").toLowerCase();
        if (e.ctrlKey && !e.altKey && !e.metaKey && klc === "a") return;
        // Preventing the iframe's default for keys we forward to the parent
        // is essential — the parent's preventDefault on the synthetic
        // keydown can't suppress the browser's reaction to the ORIGINAL
        // event (Ctrl+R reload, Ctrl+F find, etc.). Suppress here so the
        // browser doesn't act before the parent processes the action.
        var k = klc;
        var isShortcut = e.ctrlKey && !e.altKey && !e.metaKey && (
            k === "r" || k === "f" || k === "n" || k === "d" ||
            k === "z" || k === "y" || k === "k" ||
            // Ctrl+P: without this suppress, WebView2's native print fires on
            // the iframe's default and prints the WHOLE APP window instead of
            // the letter (Bob 2026-07-02). The forwarded event reaches app.ts,
            // which runs printCurrentMessage (letter + header block).
            k === "p"
        );
        if (isShortcut) e.preventDefault();
        window.parent.postMessage({
            type: "previewKey",
            key: e.key, code: e.code,
            ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey,
        }, "*");
    });
})();
</script>
</head><body>${html}</body></html>`;
}

/** Open the current message in a separate view: floating draggable overlay
 *  on desktop (multiple at once, like compose), full-screen mode on mobile.
 *  Threshold matches the layout.css responsive breakpoint so the experience
 *  is consistent with other narrow-mode behavior. Snapshot in time — the
 *  pop-out doesn't auto-update if the user clicks another message. */
export function popOutCurrentMessage(): void {
    if (!currentMessage) return;
    // Use the fullscreen toggle on any touch-primary device, not just <=768
    // px wide. Android tablets / large phones in landscape can be wider
    // than 768 but the desktop "spawn a floating draggable overlay" path
    // doesn't work well there — touch can't drag the title bar, and on
    // some WebView2 builds the overlay paints off-screen or under the
    // taskbar. Treat coarse-pointer hosts (which is what Android always
    // is) as the fullscreen case (Bob 2026-05-26 "the box with arrow
    // doesn't pop up nor go full window").
    const isAndroid = (window as any).mailxapi?.platform === "android";
    // Use the in-window fullscreen toggle ONLY for genuinely touch-only or
    // narrow hosts. A touch-CAPABLE desktop (e.g. a Windows laptop with a
    // touchscreen) matches `(pointer: coarse)` even when the user is on the
    // mouse — the old check sent it to fullscreen, which has no Reply/Forward/
    // Delete toolbar, so the popout "didn't offer Reply" (Bob 2026-06-29; same
    // misdetection class as the multi-select bug). Prefer the desktop popout
    // (with its action toolbar + working links) whenever a real pointer exists.
    const hasMouse = !!window.matchMedia?.("(any-pointer: fine)")?.matches;
    const isNarrow = window.innerWidth <= 768;
    if (isAndroid || isNarrow || !hasMouse) {
        document.body.classList.toggle("viewer-fullscreen");
        return;
    }
    // Drafts: popout means "open in compose for editing", not "open as
    // read-only window". The read-only popout has no Reply / Edit / Send
    // buttons, so handing a draft to it is a dead end. Same routing as
    // the double-click → mailx-popout-message handler in app.ts.
    const flags: string[] = Array.isArray((currentMessage as any).flags) ? (currentMessage as any).flags : [];
    if (flags.includes("\\Draft")) {
        document.dispatchEvent(new CustomEvent("mailx-popout-message", {
            detail: {
                accountId: currentAccountId,
                uid: (currentMessage as any).uid,
                folderId: (currentMessage as any).folderId,
                subject: (currentMessage as any).subject,
            },
        }));
        return;
    }
    spawnDesktopPopout(currentMessage, currentAccountId);
}

/** Pop the message COMPLETELY out into its own OS window (not the in-window
 *  overlay). The daemon spawns a NATIVE msger WebView window pointed at its
 *  loopback popout server (Bob 2026-07-06: "the 🗗 window button should use
 *  msger not the browser") — window.open is gone from this path; a browser
 *  option can come later by pointing the OS browser at the same URL. The
 *  popout page's toolbar routes Reply/Forward/etc back to the MAIN window,
 *  where the real editable compose opens. Falls back to the in-window overlay
 *  when there's no popout host (server didn't bind, non-IPC host). */
export function popOutToWindow(): void {
    if (!currentMessage) return;
    const a = currentAccountId;
    const u = (currentMessage as any).uid;
    const f = (currentMessage as any).folderId;
    const subject = (currentMessage as any).subject || "";
    void (async () => {
        try {
            const { popoutWindow } = await import("../lib/api-client.js");
            const r = await popoutWindow(a, u, f, subject);
            if (!r?.ok) {
                console.log(`[popout-window] daemon declined (${r?.reason || "no reason"}) → overlay fallback`);
                popOutCurrentMessage();
            }
        } catch (e: any) {
            console.log(`[popout-window] popoutWindow failed: ${e?.message || e} → overlay fallback`);
            popOutCurrentMessage();
        }
    })();
}

/** Print the currently-displayed message. Builds a self-contained printable
 *  document — a header block (From/To/Cc/Subject/Date) plus the message body
 *  — renders it in an off-screen iframe, and fires the print dialog. There
 *  was no way to print a letter before (Bob 2026-05-21). */
/** Full standalone HTML document for a message: printed-letter header block
 *  (From/To/Cc/Subject/Date) + body. Shared by Print and Save-as-HTML so the
 *  saved file reads exactly like the printout. */
function buildPrintableDoc(m: any): string | null {
    if (!m) return null;
    const fmt = (a: { name?: string; address?: string }): string =>
        a?.name ? `${a.name} <${a.address || ""}>` : (a?.address || "");
    const esc = escapeHtmlLocal;
    const row = (label: string, value: string): string =>
        value ? `<div><strong>${label}:</strong> ${esc(value)}</div>` : "";
    const headerHtml =
        `<div style="font-family:system-ui,Segoe UI,sans-serif;font-size:11pt;` +
        `border-bottom:1px solid #999;padding-bottom:8px;margin-bottom:14px;line-height:1.5;">` +
        row("From", fmt(m.from || {})) +
        row("To", (m.to || []).map(fmt).join(", ")) +
        (m.cc?.length ? row("Cc", m.cc.map(fmt).join(", ")) : "") +
        row("Subject", m.subject || "") +
        row("Date", m.date ? new Date(m.date).toLocaleString() : "") +
        `</div>`;
    const bodyHtml = m.bodyHtml
        ? m.bodyHtml
        : `<pre style="white-space:pre-wrap;word-break:break-word;font-family:system-ui,sans-serif;">${esc(m.bodyText || "")}</pre>`;
    return `<!DOCTYPE html><html><head><meta charset="utf-8">` +
        `<title>${esc(m.subject || "Message")}</title>` +
        `<style>@media print{body{margin:0;}}body{margin:16px;}img{max-width:100%;}</style>` +
        `</head><body>${headerHtml}${bodyHtml}</body></html>`;
}

export function printCurrentMessage(): void {
    if (!currentMessage) return;
    printMessage(currentMessage);
}

/** Print a SPECIFIC message. Split out of printCurrentMessage so the pop-out
 *  prints the message IT is showing — the pop-out is deliberately independent
 *  of the main pane (open A in a pop-out, move the main pane to B, both stay
 *  put), so printing "whatever the viewer has" would have printed the wrong
 *  letter from there. */
export function printMessage(msg: any): void {
    const doc = buildPrintableDoc(msg);
    if (!doc) return;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;left:-9999px;width:1px;height:1px;border:0;";
    iframe.srcdoc = doc;
    iframe.addEventListener("load", () => {
        const win = iframe.contentWindow;
        if (!win) { iframe.remove(); return; }
        // afterprint fires when the dialog closes (Chromium/WebView2). A
        // fallback timeout covers hosts that don't emit it so the hidden
        // iframe never leaks.
        let removed = false;
        const cleanup = (): void => { if (!removed) { removed = true; iframe.remove(); } };
        win.addEventListener("afterprint", cleanup, { once: true });
        setTimeout(cleanup, 120_000);
        try { win.focus(); win.print(); }
        catch (e) { console.error("[print] failed:", e); cleanup(); }
    }, { once: true });
    document.body.appendChild(iframe);
}

/** Save a Blob to disk. Native Save-As dialog where the File System Access
 *  API exists (WebView2/Chromium — which also stamps the file with the
 *  Mark-of-the-Web, so Office/SmartScreen treat it as untrusted); falls back
 *  to a programmatic <a download> elsewhere. */
async function saveBlobAs(suggestedName: string, blob: Blob): Promise<void> {
    const picker = (window as any).showSaveFilePicker;
    if (typeof picker === "function") {
        let handle: any;
        try {
            handle = await picker({ suggestedName });
        } catch (e: any) {
            if (e?.name === "AbortError") return; // user cancelled
            throw e;
        }
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = suggestedName; a.style.display = "none";
    document.body.appendChild(a); a.click();
    setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
}

/** Save the open message's raw RFC 822 source as a .eml file — the portable
 *  mail format Outlook / Thunderbird / other clients open directly (an .eml
 *  with multipart/related content is what .mht wraps; .eml additionally keeps
 *  the real headers and attachments). */
export async function saveCurrentMessageAsEml(): Promise<void> {
    if (!currentMessage) return;
    try {
        const src = await getMessageSource(currentAccountId, currentMessage.uid, currentMessage.folderId);
        const bytes = Uint8Array.from(atob(src.dataBase64), c => c.charCodeAt(0));
        await saveBlobAs(src.filename || "message.eml", new Blob([bytes], { type: "message/rfc822" }));
    } catch (e: any) {
        window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: `Couldn't save message: ${e?.message || e}`, key: "message-save" } }));
    }
}

/** Save the open message as a standalone HTML file — the same header block +
 *  body document the Print path renders, so the saved file reads like the
 *  printed letter. */
export async function saveCurrentMessageAsHtml(): Promise<void> {
    if (!currentMessage) return;
    const doc = buildPrintableDoc(currentMessage);
    if (!doc) return;
    const base = (currentMessage.subject || "message").replace(/[<>:"/\\|?*\r\n]/g, "_").slice(0, 80).trim() || "message";
    try {
        await saveBlobAs(`${base}.html`, new Blob([doc], { type: "text/html" }));
    } catch (e: any) {
        window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: `Couldn't save message: ${e?.message || e}`, key: "message-save" } }));
    }
}

/** Build a floating overlay carrying a snapshot of the message: header
 *  (subject, from, to, date) + sandboxed body iframe + attachment chips.
 *  Reuses the compose-overlay drag/resize/close pattern. Independent of the
 *  main viewer — opening pop-out for message A then switching the main pane
 *  to message B leaves A visible in its overlay. */
export function spawnDesktopPopout(msg: any, accountId: string): void {
    const wrapper = document.createElement("div");
    wrapper.className = "compose-overlay viewer-popout";
    wrapper.style.cssText = "position:fixed;top:60px;right:20px;width:min(720px,55vw);height:min(800px,80vh);z-index:1000;border:1px solid var(--color-border, #ccc);border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,0.3);display:flex;flex-direction:column;background:var(--color-bg, #fff);resize:both;overflow:hidden;";

    const titleBar = document.createElement("div");
    titleBar.style.cssText = "display:flex;align-items:center;justify-content:space-between;padding:6px 10px;background:var(--color-bg-alt, #e8ecf0);color:var(--color-text, #000);border-radius:8px 8px 0 0;cursor:move;user-select:none;flex-shrink:0;font-size:13px;";
    const titleText = document.createElement("span");
    titleText.style.cssText = "overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;font-weight:600;";
    titleText.textContent = msg.subject || "(no subject)";
    titleBar.appendChild(titleText);

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "✕";
    closeBtn.title = "Close pop-out";
    closeBtn.style.cssText = "background:none;border:none;font-size:16px;cursor:pointer;color:#666;padding:2px 8px;border-radius:4px;flex-shrink:0;";
    closeBtn.addEventListener("mouseenter", () => closeBtn.style.color = "#c00");
    closeBtn.addEventListener("mouseleave", () => closeBtn.style.color = "#666");
    closeBtn.addEventListener("click", () => wrapper.remove());
    titleBar.appendChild(closeBtn);

    const headerInfo = document.createElement("div");
    headerInfo.style.cssText = "padding:8px 12px;border-bottom:1px solid var(--color-border, #ddd);font-size:13px;line-height:1.4;flex-shrink:0;";
    const formatAddrLocal = (a: { name?: string; address: string }) =>
        a.name ? `${a.name} <${a.address}>` : a.address;
    const fromStr = formatAddrLocal(msg.from || { address: "" });
    const toStr = (msg.to || []).map(formatAddrLocal).join(", ");
    const ccStr = msg.cc?.length ? ` Cc: ${msg.cc.map(formatAddrLocal).join(", ")}` : "";
    const dateStr = msg.date ? new Date(msg.date).toLocaleString() : "";
    headerInfo.innerHTML =
        `<div><strong>${escapeHtmlLocal(fromStr)}</strong></div>` +
        `<div style="color:var(--color-text-muted, #666)">To: ${escapeHtmlLocal(toStr)}${escapeHtmlLocal(ccStr)}</div>` +
        `<div style="color:var(--color-text-muted, #666);font-size:12px">${escapeHtmlLocal(dateStr)}</div>`;

    const bodyContainer = document.createElement("div");
    bodyContainer.style.cssText = "flex:1;overflow:hidden;display:flex;";
    if (msg.bodyHtml) {
        const iframe = document.createElement("iframe");
        iframe.sandbox.add("allow-same-origin");
        iframe.sandbox.add("allow-popups");
        iframe.sandbox.add("allow-popups-to-escape-sandbox");
        iframe.sandbox.add("allow-top-navigation-by-user-activation");
        iframe.sandbox.add("allow-scripts");
        iframe.srcdoc = wrapHtmlBody(msg.bodyHtml, msg.remoteAllowed);
        iframe.style.cssText = "flex:1;border:none;width:100%;background:#fff;";
        bodyContainer.appendChild(iframe);
        // Same zoom + key-forwarding controls as the main viewer iframe —
        // Ctrl+wheel / Ctrl± / Ctrl+0 and the persisted `mailx-preview-zoom`.
        // Without this the pop-out had no zoom at all (Bob 2026-05-21).
        installPreviewControls(iframe);
    } else {
        const pre = document.createElement("pre");
        pre.style.cssText = "padding:12px;white-space:pre-wrap;word-break:break-word;margin:0;flex:1;overflow:auto;";
        pre.textContent = msg.bodyText || "(no content)";
        bodyContainer.appendChild(pre);
    }

    // Drag — same pattern as compose-overlay: pointer-events:none on the
    // iframe so cursor crossing into it doesn't lose drag events.
    let dragX = 0, dragY = 0;
    titleBar.addEventListener("mousedown", (e: MouseEvent) => {
        if (e.target === closeBtn) return;
        e.preventDefault();
        const rect = wrapper.getBoundingClientRect();
        dragX = e.clientX - rect.left;
        dragY = e.clientY - rect.top;
        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
        bodyContainer.style.pointerEvents = "none";
        document.body.style.userSelect = "none";
        const onMove = (ev: MouseEvent) => {
            ev.preventDefault();
            const left = clamp(ev.clientX - dragX, 0, window.innerWidth - 40);
            const top = clamp(ev.clientY - dragY, 0, window.innerHeight - 40);
            wrapper.style.left = `${left}px`;
            wrapper.style.top = `${top}px`;
            wrapper.style.right = "auto";
            wrapper.style.bottom = "auto";
        };
        const onUp = () => {
            bodyContainer.style.pointerEvents = "";
            document.body.style.userSelect = "";
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    });

    // Bring to front on click — shared with compose-overlay so they all
    // restack uniformly.
    wrapper.addEventListener("mousedown", () => {
        document.querySelectorAll(".compose-overlay").forEach(el => (el as HTMLElement).style.zIndex = "1000");
        wrapper.style.zIndex = "1001";
    });

    // Cascade pop-outs so they don't all stack at the same coords.
    const existing = document.querySelectorAll(".viewer-popout").length;
    if (existing > 0) {
        wrapper.style.top = `${60 + existing * 28}px`;
        wrapper.style.right = `${20 + existing * 28}px`;
    }

    // Action toolbar — icon buttons that MATCH the preview's `.mv-toolbar`
    // (same glyphs/titles) so the popped-out message reads identically to the
    // inline preview, instead of a divergent text toolbar (Bob 2026-06-29: "why
    // isn't that toolbar shared?"). Each button fires `mailx-popout-action`
    // (the popout can't reach app.ts's openCompose directly); app.ts routes it.
    const toolbar = document.createElement("div");
    toolbar.className = "mv-toolbar";
    toolbar.style.cssText = "display:flex;gap:4px;padding:6px 12px;border-bottom:1px solid var(--color-border, #ddd);flex-shrink:0;";
    const mkPopoutBtn = (glyph: string, title: string, onClick: () => void): HTMLButtonElement => {
        const b = document.createElement("button");
        b.className = "tb-btn";
        b.textContent = glyph;
        b.title = title;
        b.style.cssText = "padding:4px 9px;font-size:15px;cursor:pointer;border:1px solid var(--color-border, #ccc);border-radius:4px;background:var(--color-bg, #fff);color:var(--color-text, #000);";
        b.addEventListener("click", onClick);
        return b;
    };
    const firePopoutAction = (action: string): void => {
        document.dispatchEvent(new CustomEvent("mailx-popout-action", { detail: { action, msg, accountId } }));
    };
    toolbar.appendChild(mkPopoutBtn("↩", "Reply (Ctrl+R)", () => firePopoutAction("reply")));
    toolbar.appendChild(mkPopoutBtn("↩↩", "Reply All (Ctrl+Shift+R)", () => firePopoutAction("replyAll")));
    toolbar.appendChild(mkPopoutBtn("→", "Forward", () => firePopoutAction("forward")));
    toolbar.appendChild(mkPopoutBtn("⧉", "Edit as new message", () => firePopoutAction("editAsNew")));
    toolbar.appendChild(mkPopoutBtn("★", "Flag", () => firePopoutAction("flag")));
    toolbar.appendChild(mkPopoutBtn("🗑", "Delete", () => {
        firePopoutAction("delete");
        wrapper.remove();   // the message is gone — close its window too
    }));

    wrapper.appendChild(titleBar);
    wrapper.appendChild(headerInfo);
    wrapper.appendChild(toolbar);
    wrapper.appendChild(bodyContainer);
    document.body.appendChild(wrapper);
}

function escapeHtmlLocal(s: string): string {
    return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Background body-fetch failure — when the user clicked an uncached row
// and the reconciler's IMAP fetch failed (rate limit, network drop, auth),
// surface a banner above the (empty) body for the *currently-shown* message
// so the user knows the spinner isn't going to resolve. Other windows /
// stale rows are ignored — no banner clutter for messages they walked away
// from. The reconciler retries on its own cadence; no client-side retry.
subscribeStore("*", (ev: any) => {
    if (ev.kind !== "bodyFetchError") return;
    // Always record — even if the failed message isn't the current
    // viewer target — so a later click on that row can surface the
    // reason instead of "No content".
    recentFetchErrors.set(`${ev.accountId}:${ev.uid}`, {
        error: String(ev.error || "fetch failed"),
        transient: !!ev.transient,
        when: Date.now(),
    });
    if (!currentMessage || currentAccountId !== ev.accountId) return;
    if (currentMessage.uid !== ev.uid) return;
    const bodyEl = document.getElementById("mv-body");
    if (!bodyEl) return;
    if (bodyEl.querySelector(".mv-body-fetch-error")) return; // already shown
    const banner = document.createElement("div");
    banner.className = "mv-system-message mv-system-error mv-body-fetch-error";
    const transientNote = ev.transient
        ? "Will retry automatically."
        : "Move/delete may have raced with the server.";
    banner.innerHTML = `
        <div class="mv-system-tag">mailx</div>
        <div class="mv-system-title">Couldn't fetch message body</div>
        <div class="mv-system-body">${escapeHtmlLocal(String(ev.error || "fetch failed"))}<br><span style="color:var(--color-text-muted);font-size:0.9em">${transientNote}</span></div>`;
    const placeholder = bodyEl.querySelector(".mv-empty");
    if (placeholder) placeholder.replaceWith(banner);
    else bodyEl.prepend(banner);
});

// `draftSaved` arrives when the compose window's autosave or Ctrl+S lands
// new body content on disk for the message currently displayed in the
// preview pane. Without re-rendering, the preview shows the BEFORE-save
// body until the user clicks the row again. Match by `previousDraftUid` —
// that's the UID currently displayed; the daemon will eventually IMAP
// APPEND a fresh UID and delete the old one, but until that round-trip
// lands `currentMessage.uid` still refers to the old UID.
onEvent((ev: any) => {
    if (!ev || ev.type !== "draftSaved") return;
    if (!currentMessage || currentAccountId !== ev.accountId) return;
    // 2026-09-16 — Claude Code (Fable 5.1). Match on the draft's stable id
    // first: every checkpoint re-appends under a new UID, so a UID-only match
    // held for exactly one save. The UID remains as a fallback for rows whose
    // header parse hasn't landed yet.
    const sameDraft = !!ev.draftId && (currentMessage as any).mailxDraftId === ev.draftId;
    if (!sameDraft && currentMessage.uid !== ev.previousDraftUid) return;
    const newBody = String(ev.bodyHtml || "");
    currentMessage.bodyHtml = newBody;
    if (ev.subject) currentMessage.subject = ev.subject;
    const bodyEl = document.getElementById("mv-body");
    const iframe = bodyEl?.querySelector("iframe") as HTMLIFrameElement | null;
    if (iframe) {
        iframe.srcdoc = wrapHtmlBody(newBody, !!(currentMessage as any).remoteAllowed);
    }
    const subjEl = document.querySelector(".mv-subject");
    if (subjEl && ev.subject) subjEl.textContent = String(ev.subject);
});
